export type Language = 'en' | 'de' | 'fr' | 'it' | 'ta';

export const translations = {
  en: {
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      search: 'Search',
      loading: 'Loading...',
      all: 'All',
      confirm: 'Confirm',
      pending: 'Pending',
      cancelled: 'Cancelled',
      confirmed: 'Confirmed',
      back: 'Back',
      continue: 'Continue',
      processing: 'Processing...',
      saving: 'Saving...',
      total: 'Total',
      status: 'Status',
      actions: 'Actions',
      view: 'View',
      edit: 'Edit',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      date: 'Date',
      time: 'Time',
      price: 'Price',
      duration: 'Duration',
      category: 'Category',
      description: 'Description',
      available: 'Available',
      booked: 'Booked',
      sharp: 'Sharp',
      cuts: 'Cuts',
      est: 'Est. 2024 • Premium Barbering',
      currency: 'CHF',
      none: 'None',
      selectLanguage: 'Select Language',
      na: 'N/A',
      noAddress: 'No address provided',
      barber: 'Barber',
      barbers: 'Barbers',
      today: 'Today',
      min: 'Minutes',
      any: 'Any',
      away: 'away',
      customer: 'Customer',
      copyright: '© {year} Sharp Cuts. All rights reserved.',
      terms: 'Terms & Conditions',
      privacy: 'Privacy Policy',
      cookies: 'Cookie Policy',
      swissLaw: 'Swiss Law Compliant',
      validation: {
        required: 'This field is required',
        email: 'Invalid email address',
        phone: 'Invalid Swiss phone number',
        passwordMin: 'Password must be at least 8 characters',
        passwordMatch: 'Passwords do not match',
        nameMin: 'Name must be at least 2 characters'
      },
      days: {
        sun: 'Sun',
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat'
      }
    },
    setup: {
      required: 'Setup Required',
      dbError: 'Database Error',
      configureSupabase: 'Please configure your Supabase credentials in .env to start the barber shop system.',
      requiredVariables: 'Required Variables:',
      nextSteps: 'Next Steps:',
      openDashboard: 'Open your Supabase Dashboard',
      goSqlEditor: 'Go to the SQL Editor',
      runSchema: 'Paste and run the contents of supabase_schema.sql',
      refreshPage: 'Refresh this page',
      dbSchemaNotFound: 'Database schema not found. Please run the SQL schema in your Supabase dashboard.',
    },
    nav: {
      home: 'Home',
      dashboard: 'Dashboard',
      logout: 'Logout',
      login: 'Login',
      register: 'Register',
      booking: 'Booking',
      book: 'Book',
      history: 'Account',
      admin: 'Admin',
      systemAdmin: 'System Admin',
      becomePartner: 'Become a Partner',
      switchLight: 'Switch to light mode',
      switchDark: 'Switch to dark mode',
    },
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      createAccount: 'Create Account',
      alreadyHaveAccount: 'Already have an account? Sign In',
      dontHaveAccount: "Don't have an account? Sign Up",
      password: 'Password',
      confirmPassword: 'Confirm Password',
      passwordsDoNotMatch: 'Passwords do not match',
      verifyEmail: 'Verify Your Email',
      checkEmail: "We've sent a secure link to your email. Please click the link to activate your account.",
      didntReceive: "Didn't receive it?",
      resendEmail: 'Resend Confirmation Email',
      resendIn: 'Resend in {seconds}s',
      backToSignIn: 'Back to Sign In',
      checkSpam: 'Check your spam or junk folder',
      waitMinutes: 'Wait a few minutes for the delivery',
      ensureEmail: 'Ensure the email address is correct',
      adminAccess: 'Admin Access',
      joinCrew: 'Join the Crew',
      signInToManage: 'Sign in to manage your shop',
      customerLogin: 'Customer Login',
      signUpToBook: 'Create an account to manage your bookings',
      signInToBook: 'Sign in to manage your bookings',
      strengthVeryWeak: 'Very Weak',
      strengthWeak: 'Weak',
      strengthMedium: 'Medium',
      strengthStrong: 'Strong',
      strengthVeryStrong: 'Very Strong',
      forgotPassword: 'Forgot Password?',
      resetPassword: 'Reset Password',
      sendResetLink: 'Send Reset Link',
      resetLinkSent: 'Password reset link sent! Please check your email.',
      newPassword: 'New Password',
      updatePassword: 'Update Password',
      passwordUpdated: 'Password updated successfully!',
      selectShopToAuth: 'Please select a shop to continue',
      authFor: 'Authentication for {shop}',
      emailConfirmed: 'Email confirmed successfully! You can now sign in.',
    },
    booking: {
      bookNow: 'Book Now',
      myBookings: 'My Bookings',
      chooseServices: 'Choose Services',
      pickTime: 'Pick a Time',
      yourDetails: 'Your Details',
      confirmBooking: 'Confirm Booking',
      stepOf: 'Step {step} of {total}',
      selectShop: 'Select a Shop',
      selectBarber: 'Select a Barber',
      chooseLocation: 'Choose your preferred location to book an appointment.',
      chooseBarber: 'Choose your preferred barber for your appointment.',
      changeShop: 'Change Shop',
      changeBarber: 'Change Barber',
      totalSelection: 'Total Selection',
      selectDate: 'Select Date',
      availableSlots: 'Available Slots',
      noSlots: 'No slots available for this day.',
      noBarbers: 'No barbers found for this shop.',
      finalDetails: 'Final Details',
      yourInformation: 'Your Information',
      provideDetails: 'Please provide your contact details for the booking.',
      fullName: 'Full Name',
      emailAddress: 'Email Address',
      phoneNumber: 'Phone Number',
      firstName: 'First Name',
      lastName: 'Last Name',
      companyName: 'Company Name (Barber Shop Name)',
      address: 'Address',
      reviewBooking: 'Review Booking',
      changeTime: 'Change Time',
      confirmYourBooking: 'Confirm Your Booking',
      reviewBeforeSubmission: 'Please review all details before final submission.',
      selectedServices: 'Selected Services',
      shopAndTime: 'Shop & Time',
      customerInformation: 'Customer Information',
      confirmAndBook: 'Confirm & Book Now',
      editDetails: 'Edit Details',
      cancellationPolicy: 'Cancellation Policy',
      cancellationTerms: 'You can cancel or reschedule your appointment up to 24 hours before the scheduled time. Cancellations made within 24 hours will incur a 50% fee.',
      acceptPolicy: 'I agree to the cancellation policy',
      youAreSharp: "You're Sharp!",
      confirmationSent: "We've sent a confirmation email to {email}.",
      seeYouAt: "See you at {shop} on {date} at {time}!",
      bookAnother: 'Book Another',
      yourBookings: 'Your Bookings',
      manageAppointments: 'View and manage your upcoming appointments.',
      noBookings: 'No bookings found.',
      cancelBooking: 'Cancel Booking',
      cancelConfirm: 'Are you sure you want to cancel this booking?',
      manageBookingsPrompt: 'Want to manage your bookings easily?',
      loginOrSignUp: 'Log in or Sign up',
      continueAsGuest: 'Continue as Guest',
      agreeToTerms: 'I agree to the {terms} and {privacy}',
      mustAgree: 'You must agree to the terms and conditions to continue',
      bookSlot: 'Book Slot',
      rateExperience: 'Rate Your Experience',
      shareFeedback: 'Share your feedback with us...',
      leaveReview: 'Leave a Review',
      reviewSubmitted: 'Review submitted successfully',
      excellent: 'Excellent',
      veryGood: 'Very Good',
      good: 'Good',
      fair: 'Fair',
      poor: 'Poor',
      promoCode: 'Promo Code',
      apply: 'Apply',
      invalidPromo: 'Invalid or expired promo code',
      discountApplied: 'Discount applied!',
    },
    pricing: {
      title: 'Elevate Your Barber Business',
      subtitle: 'Join the elite network of barber shops using our premium management system. Streamline your bookings, manage your staff, and grow your brand.',
      proPlan: 'Pro Barber Shop',
      monthlySub: 'Monthly Subscription',
      getStarted: 'Get Started Now',
      cancelAnytime: 'Cancel anytime • Secure payment via Stripe',
      features: {
        unlimitedBarbers: 'Unlimited Barbers',
        unlimitedBookings: 'Unlimited Bookings',
        customServices: 'Custom Services & Categories',
        realtimeDash: 'Real-time Dashboard',
        emailNotifications: 'Email Notifications',
        multiLanguage: 'Multi-language Support',
        prioritySupport: 'Priority Support',
        advancedAnalytics: 'Advanced Analytics',
        upTo3Barbers: 'Up to 3 Barbers',
        standardAnalytics: 'Standard Analytics',
      },
      saveTwoMonths: 'Save 2 months',
      fourteenDayTrial: '14-day free trial',
      noChargeToday: 'No charge today • Trial starts now',
      billedMonthly: 'Billed monthly',
      billedAnnually: 'Billed annually',
      monthly: 'Monthly',
      annually: 'Annually',
      basicDesc: 'Perfect for small shops starting out',
      proDesc: 'The complete solution for growing shops',
      mostPopular: 'Most Popular',
      iAgreeTo: 'I AGREE TO THE',
      and: 'AND',
      subRequired: 'Subscription Required',
      subRequiredDesc: 'To access the admin dashboard and manage your shop, you need an active subscription.',
      viewPricing: 'View Pricing',
      totalPerYear: 'Total: {price} / year',
      perMonth: 'per month',
      perMonthBilledAnnually: 'per month, billed annually',
      startTrial: 'Start 14-Day Free Trial',
      authRequired: 'Authentication Required',
      authDesc: 'Please sign in or create an account to start your subscription.',
    },
    legal: {
      terms: 'Terms and Conditions',
      privacy: 'Privacy Policy',
      cookies: 'Cookie Policy',
      cookieConsent: 'We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.',
      accept: 'Accept',
      decline: 'Decline',
    },
    admin: {
      reservations: 'Reservations',
      noReservations: 'No reservations found',
      customers: 'Customers',
      slots: 'Slots',
      services: 'Services',
      team: 'Team',
      shops: 'Shops',
      settings: 'Settings',
      roles: 'Roles',
      userRoles: 'User Roles',
      platformUsers: 'Platform Users',
      systemOverview: 'System Overview',
      activeSubscriptions: 'Active Subscriptions',
      totalPlatformUsers: 'Total Platform Users',
      platformHealth: 'Platform Health',
      platformHealthDesc: 'Your platform is currently performing at optimal levels. All systems are operational.',
      systemStable: 'System Stable',
      billingTransactions: 'Billing Transactions',
      recentBilling: 'Recent Billing',
      viewAllTransactions: 'View All Transactions',
      shopLocationUpdated: 'Shop location updated',
      failedUpdateLocation: 'Failed to update shop location',
      enterValidCoords: 'Please enter valid coordinates',
      deleteShopConfirm: 'Are you sure you want to delete this shop? This action cannot be undone.',
      shopControl: 'Shop Control',
      superAdmin: 'Super Admin',
      barberAdmin: 'Shop Admin',
      barberStaff: 'Barber Staff',
      customerRole: 'Customer',
      status: 'STATUS',
      myDashboard: 'MY DASHBOARD',
      chat: 'CHAT',
      adminDashboard: 'Admin Dashboard',
      superAdminDashboard: 'Super Admin Dashboard',
      menuItems: 'Menu Items',
      instantSetup: 'Instant Setup',
      generateSlots: 'Generate Slots',
      startTime: 'Start Time',
      endTime: 'End Time',
      intervalMins: 'Interval (mins)',
      customInterval: 'Custom Interval',
      service: 'Service',
      breakStart: 'Break Start',
      breakEnd: 'Break End',
      clearAvailability: 'Clear Availability',
      confirmClearAvailability: 'Are you sure you want to delete all available slots for this day?',
      slotsGenerated: 'Slots generated successfully',
      failedGenerateSlots: 'Failed to generate slots',
      availabilityCleared: 'Availability cleared',
      failedClearAvailability: 'Failed to clear availability',
      description: 'Service Description',
      addService: 'Add Service',
      searchServices: 'Search services...',
      searchTeam: 'Search team...',
      searchReservations: 'Search by name, email or phone...',
      searchShops: 'Search shops...',
      searchUsers: 'Search users...',
      searchTransactions: 'Search transactions...',
      totalBookings: 'Total Bookings',
      lastBooking: 'Last Booking',
      recentReservations: 'Recent Reservations',
      customer: 'Customer',
      dateAndTime: 'Date & Time',
      addSlot: 'Add Slot',
      manageStaff: 'Manage staff and administrator roles',
      allRoles: 'All Roles',
      allCategories: 'All Categories',
      allBarbers: 'All Barbers',
      shop: 'Shop',
      allShops: 'All Shops (Overview)',
      signOut: 'Sign Out',
      profileNotFound: 'Profile Not Found',
      accessDenied: 'Access Denied',
      selectCategory: 'Select Category',
      haircut: 'Haircut',
      shave: 'Shave',
      styling: 'Styling',
      treatment: 'Treatment',
      joined: 'Joined',
      currentRole: 'Current Role',
      barberShop: 'Barber Shop',
      actions: 'Actions',
      makeSuperAdmin: 'Make Super Admin',
      makeAdmin: 'Make Shop Admin',
      makeStaff: 'Make Barber Staff',
      revoke: 'Revoke',
      noShopAssigned: 'No Shop Assigned',
      noShop: 'No Shop',
      updateRole: 'Update User Role',
      confirmUpdateRole: 'Are you sure you want to change this user\'s role? This may grant or restrict access to administrative features.',
      currentUser: 'Current User',
      manageSystemRoles: 'Manage system-wide user permissions',
      general: 'General Info',
      addressLocation: 'Address & Location',
      operatingHours: 'Operating Hours',
      bookingReminders: 'Booking & Reminders',
      hours: 'Hours',
      shopStatus: 'Shop Status',
      activateDeactivateShop: 'Enable or disable this shop',
      manageShopDetails: 'Manage your barber shop details',
      shopName: 'Shop Name',
      address: 'Address',
      openingTime: 'Opening Time',
      closingTime: 'Closing Time',
      appointmentReminders: 'Appointment Reminders',
      enableReminders: 'Enable Reminders',
      enableRemindersDesc: 'Send automated reminders to customers',
      reminderType: 'Reminder Type',
      emailOnly: 'Email Only',
      smsOnly: 'SMS Only',
      bothEmailSms: 'Email & SMS',
      reminderTypeDesc: 'Choose how customers receive their appointment reminders',
      hoursBefore: 'Hours Before',
      sendingReminders: 'Sending Reminders...',
      sendRemindersManual: 'Send Reminders Now (Manual Trigger)',
      longitude: 'Longitude',
      latitude: 'Latitude',
      updateShopDetails: 'Update Shop Details',
      onlyAdminsModify: 'Only shop administrators can modify these settings.',
      noShopSelected: 'No shop selected or assigned.',
      user: 'User',
      category: 'Category',
      min: 'Minutes',
      photoUrl: 'Photo',
      price: 'Price',
      bio: 'Bio',
      linkUser: 'Link to User Account',
      noUser: 'No User Linked',
      addBarber: 'Add Barber',
      editBarber: 'Edit Barber',
      editLocation: 'Edit Location',
      barberAdded: 'Barber added successfully',
      barberUpdated: 'Barber updated successfully',
      barberDeleted: 'Barber deleted successfully',
      linkedBarber: 'Linked Barber',
      break: 'Break Times',
      changePhoto: 'Change Photo',
      uploadPhoto: 'Upload Photo',
      photoUploadHint: 'Recommended: Square aspect ratio, max 2MB',
      failedAddBarber: 'Failed to add barber',
      failedUpdateBarber: 'Failed to update barber',
      failedDeleteBarber: 'Failed to delete barber',
      deleteBarber: 'Delete Barber',
      confirmDeleteBarber: 'Are you sure you want to delete this barber?',
      deleteService: 'Delete Service',
      confirmDeleteService: 'Are you sure you want to delete this service?',
      deleteSlot: 'Delete Slot',
      confirmDeleteSlot: 'Are you sure you want to delete this time slot?',
      cancelReservation: 'Cancel Reservation',
      confirmCancelReservation: 'Are you sure you want to cancel this reservation?',
      roleUpdated: 'Role updated to {role}',
      reservationStatusUpdated: 'Reservation status updated to {status}',
      reservationUpdated: 'Reservation updated successfully',
      failedUpdateReservation: 'Failed to update reservation',
      editReservation: 'Edit Reservation',
      customerDetails: 'Customer Details',
      newReservation: 'New Reservation',
      newBookingMade: 'A new booking has been made.',
      failedUpdateRole: 'Failed to update role',
      categories: 'Categories',
      addCategory: 'Add Category',
      categoryExists: 'Category already exists',
      categoryAdded: 'Category added successfully',
      categoryDeleted: 'Category deleted successfully',
      categoryUpdated: 'Category updated successfully',
      failedUpdateCategory: 'Failed to update category',
      categoryInUse: 'Cannot delete category that is in use',
      addNewCategory: 'Add New Category...',
      selectShopFirst: 'Please select a shop first',
      slotExists: 'This time slot already exists',
      slotAdded: 'Slot added successfully',
      failedAddSlot: 'Failed to add slot',
      slotDeleted: 'Slot deleted successfully',
      failedDeleteSlot: 'Failed to delete slot',
      failedUpdateStatus: 'Failed to update status',
      selectBarberFirst: 'Please select a barber first',
      invalidPriceDuration: 'Invalid price or duration',
      serviceAdded: 'Service added successfully',
      serviceUpdated: 'Service updated successfully',
      failedAddService: 'Failed to add service',
      failedUpdateService: 'Failed to update service',
      serviceDeleted: 'Service deleted successfully',
      failedDeleteService: 'Failed to delete service',
      shopNameRequired: 'Shop name is required',
      invalidClosingTime: 'Closing time must be after opening time',
      shopAdded: 'Shop added successfully',
      failedAddShop: 'Failed to add shop',
      confirmDeleteShop: 'Are you sure you want to delete this shop?',
      shopDeleted: 'Shop deleted successfully',
      failedDeleteShop: 'Failed to delete shop',
      deleteShop: 'Delete Shop',
      shopUpdated: 'Shop updated successfully',
      failedUpdateShop: 'Failed to update shop',
      remindersNotEnabled: 'Reminders are not enabled for this shop',
      failedSendReminders: 'Failed to send reminders',
      reminderSent: 'Reminder sent successfully',
      remindersSent: '{count} reminders sent successfully',
      reminder: 'Reminder',
      sent: 'Sent',
      notSent: 'Not Sent',
      sendReminder: 'Send Reminder',
      profileNotFoundDesc: 'We could not find your user profile.',
      accessDeniedDesc: 'You do not have permission to access this page.',
      control: 'Control',
      allStatus: 'All Status',
      dateTime: 'Date & Time',
      addSlots: 'Add Slots',
      timeSlot: 'Time Slot',
      availability: 'Availability',
      manageStaffAvailability: 'Manage staff and shop availability',
      slotsForDay: 'Slots for Selected Day',
      for: 'for',
      showingSlotsFor: 'Showing slots for',
      noSlotsAdded: 'No slots added for this day',
      addFirst: 'Add Slots',
      availableSlots: 'Available slots',
      teamManagement: 'Team Management',
      manageStaffRoles: 'Manage staff and administrator roles',
      addStaff: 'Add Staff Account',
      staffAccountCreated: 'Staff account created successfully',
      failedCreateStaff: 'Failed to create staff account',
      staffCreationNotice: 'This will create a new login account for your staff member. They will be automatically assigned to your shop.',
      createAccount: 'Create Account',
      instructions: 'Instructions',
      teamInstructions: 'Manage your team members and assign them to specific shops. Admins can manage services and slots for their assigned shop.',
      menu: 'Menu',
      items: 'Items',
      subscription: 'Subscription',
      subscriptionManagement: 'Subscription Management',
      currentPlan: 'Current Plan',
      renewsOn: 'Renews on',
      manageSubscription: 'Manage Subscription',
      trialEndsOn: 'Trial ends on',
      changeShopPlan: 'Change Shop Plan',
      cancelShopSubscription: 'Cancel Shop Subscription',
      manualPlanUpdate: 'Manual Plan Update',
      confirmManualUpdate: 'Are you sure you want to manually change this plan? This will override Stripe settings.',
      confirmManualCancel: 'Are you sure you want to manually cancel this subscription?',
      basicPlan: 'Basic Barber Shop',
      proPlan: 'Pro Barber Shop',
      planUpdated: 'Plan updated successfully',
      serviceName: 'Service Name',
      opening: 'Opening',
      closing: 'Closing',
      barber: 'Barber',
      inventory: 'Inventory',
      marketing: 'Marketing',
      analytics: 'Analytics',
      commissions: 'Commissions',
      stock: 'Stock',
      minStock: 'Min. Stock',
      quantity: 'Quantity',
      unit: 'Unit',
      addPromo: 'Add Promo Code',
      code: 'Code',
      discount: 'Discount',
      expires: 'Expires',
      active: 'Active',
      revenue: 'Revenue',
      growth: 'Growth',
      monthlyGrowth: 'Monthly Growth',
      topServices: 'Top Services',
      topBarbers: 'Top Barbers',
      totalRevenue: 'Total Revenue',
      avgRating: 'Avg. Rating',
      activeCustomers: 'Active Customers',
      revenueLast30Days: 'Revenue (Last 30 Days)',
      bookingsByDay: 'Bookings by Day',
      popularServices: 'Popular Services',
      revenueByBarber: 'Revenue by Barber',
      commissionRate: 'Commission Rate',
      earned: 'Earned',
      paid: 'Paid',
      pending: 'Pending',
      cancelled: 'Cancelled',
      generateForDays: 'Days to generate',
      skipWeekends: 'Skip Weekends',
      bulkCreateAvailability: 'Bulk Create Availability',
      workingDays: 'Working Days',
      startDate: 'Start Date',
      endDate: 'End Date',
      quickTips: 'Quick Tips',
      tip1: 'Select a barber first to manage their specific availability.',
      tip2: 'Use the bulk generator to quickly set up a full month of slots.',
      tip3: 'Click on a booked slot to view appointment details.',
      noSlotsShort: 'No Slots',
      addItem: 'Add Item',
      addShop: 'Add Shop',
      automatedCampaigns: 'Automated Campaigns',
      campaignsDesc: 'Set up automatic marketing messages',
      promoCodes: 'Promo Codes',
      lowStock: 'Low Stock',
      inStock: 'In Stock',
      item: 'Item',
      amount: 'Amount',
    },
    home: {
      heroTitle: 'Vibrant Barber Experience',
      heroSubtitle: 'Book your next cut with the best in town. Professional service, modern style.',
      bookNow: 'Book Now',
      ourServices: 'Our Services',
      contactUs: 'Contact Us',
    }
  },
  de: {
    common: {
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      search: 'Suchen',
      loading: 'Laden...',
      all: 'Alle',
      confirm: 'Bestätigen',
      pending: 'Ausstehend',
      cancelled: 'Abgesagt',
      confirmed: 'Bestätigt',
      back: 'Zurück',
      continue: 'Weiter',
      processing: 'Verarbeitung...',
      saving: 'Speichern...',
      total: 'Gesamt',
      status: 'Status',
      actions: 'Aktionen',
      view: 'Ansehen',
      edit: 'Bearbeiten',
      name: 'Name',
      email: 'E-Mail',
      phone: 'Telefon',
      address: 'Adresse',
      date: 'Datum',
      time: 'Zeit',
      price: 'Preis',
      duration: 'Dauer',
      category: 'Kategorie',
      description: 'Beschreibung',
      available: 'Verfügbar',
      booked: 'Gebucht',
      sharp: 'Scharf',
      cuts: 'Schnitte',
      est: 'Seit 2024 • Premium-Barbierkunst',
      currency: 'CHF',
      none: 'Keine',
      selectLanguage: 'Sprache wählen',
      na: 'N/V',
      noAddress: 'Keine Adresse angegeben',
      today: 'Heute',
      min: 'Minuten',
      any: 'Beliebig',
      away: 'entfernt',
      customer: 'Kunde',
      copyright: '© {year} Sharp Cuts. Alle Rechte vorbehalten.',
      terms: 'AGB',
      privacy: 'Datenschutz',
      cookies: 'Cookie-Richtlinie',
      swissLaw: 'Konform mit Schweizer Recht',
      validation: {
        required: 'Dieses Feld ist obligatorisch',
        email: 'Ungültige E-Mail-Adresse',
        phone: 'Ungültige Schweizer Telefonnummer',
        passwordMin: 'Passwort muss mindestens 8 Zeichen lang sein',
        passwordMatch: 'Passwörter stimmen nicht überein',
        nameMin: 'Name muss mindestens 2 Zeichen lang sein'
      },
      days: {
        sun: 'So',
        mon: 'Mo',
        tue: 'Di',
        wed: 'Mi',
        thu: 'Do',
        fri: 'Fr',
        sat: 'Sa'
      }
    },
    setup: {
      required: 'Einrichtung erforderlich',
      dbError: 'Datenbankfehler',
      configureSupabase: 'Bitte konfigurieren Sie Ihre Supabase-Anmeldedaten in der .env-Datei, um das Barbershop-System zu starten.',
      requiredVariables: 'Erforderliche Variablen:',
      nextSteps: 'Nächste Schritte:',
      openDashboard: 'Öffnen Sie Ihr Supabase-Dashboard',
      goSqlEditor: 'Gehen Sie zum SQL-Editor',
      runSchema: 'Kopieren Sie den Inhalt von supabase_schema.sql und führen Sie ihn aus',
      refreshPage: 'Diese Seite aktualisieren',
      dbSchemaNotFound: 'Datenbankschema nicht gefunden. Bitte führen Sie das SQL-Schema in Ihrem Supabase-Dashboard aus.',
    },
    nav: {
      home: 'Startseite',
      dashboard: 'Dashboard',
      logout: 'Abmelden',
      login: 'Anmelden',
      register: 'Registrieren',
      booking: 'Buchung',
      book: 'Buchen',
      history: 'Konto',
      admin: 'Admin',
      systemAdmin: 'Systemverwaltung',
      becomePartner: 'Partner werden',
      switchLight: 'In den hellen Modus wechseln',
      switchDark: 'In den dunklen Modus wechseln',
    },
    auth: {
      signIn: 'Anmelden',
      signUp: 'Registrieren',
      createAccount: 'Konto erstellen',
      alreadyHaveAccount: 'Haben Sie bereits ein Konto? Anmelden',
      dontHaveAccount: 'Haben Sie noch kein Konto? Registrieren',
      password: 'Passwort',
      confirmPassword: 'Passwort bestätigen',
      passwordsDoNotMatch: 'Passwörter stimmen nicht überein',
      verifyEmail: 'E-Mail verifizieren',
      checkEmail: 'Wir haben einen sicheren Link an Ihre E-Mail gesendet. Bitte klicken Sie auf den Link, um Ihr Konto zu aktivieren.',
      didntReceive: 'Nicht erhalten?',
      resendEmail: 'Bestätigungs-E-Mail erneut senden',
      resendIn: 'Erneut senden in {seconds}s',
      backToSignIn: 'Zurück zur Anmeldung',
      checkSpam: 'Überprüfen Sie Ihren Spam- oder Junk-Ordner',
      waitMinutes: 'Warten Sie einige Minuten auf die Zustellung',
      ensureEmail: 'Stellen Sie sicher, dass die E-Mail-Adresse korrekt ist',
      adminAccess: 'Admin-Zugang',
      joinCrew: 'Tritt der Crew bei',
      signInToManage: 'Melden Sie sich an, um Ihren Shop zu verwalten',
      customerLogin: 'Kunden-Login',
      signUpToBook: 'Erstellen Sie ein Konto, um Ihre Buchungen zu verwalten',
      signInToBook: 'Melden Sie sich an, um Ihre Buchungen zu verwalten',
      strengthVeryWeak: 'Sehr schwach',
      strengthWeak: 'Schwach',
      strengthMedium: 'Mittel',
      strengthStrong: 'Stark',
      strengthVeryStrong: 'Sehr stark',
      forgotPassword: 'Passwort vergessen?',
      resetPassword: 'Passwort zurücksetzen',
      sendResetLink: 'Reset-Link senden',
      resetLinkSent: 'Link zum Zurücksetzen des Passworts gesendet! Bitte überprüfen Sie Ihre E-Mails.',
      newPassword: 'Neues Passwort',
      updatePassword: 'Passwort aktualisieren',
      passwordUpdated: 'Passwort erfolgreich aktualisiert!',
      selectShopToAuth: 'Bitte wählen Sie ein Geschäft aus, um fortzufahren',
      authFor: 'Anmeldung für {shop}',
      emailConfirmed: 'E-Mail erfolgreich bestätigt! Sie können sich jetzt anmelden.',
    },
    booking: {
      bookNow: 'Jetzt buchen',
      myBookings: 'Meine Buchungen',
      chooseServices: 'Dienstleistungen wählen',
      pickTime: 'Zeit wählen',
      yourDetails: 'Ihre Details',
      confirmBooking: 'Buchung bestätigen',
      stepOf: 'Schritt {step} von {total}',
      selectShop: 'Filiale auswählen',
      selectBarber: 'Wählen Sie einen Barbier',
      chooseLocation: 'Wählen Sie Ihren bevorzugten Standort, um einen Termin zu buchen.',
      chooseBarber: 'Wählen Sie Ihren bevorzugten Barbier für Ihren Termin.',
      changeShop: 'Shop ändern',
      changeBarber: 'Barbier ändern',
      totalSelection: 'Gesamtauswahl',
      selectDate: 'Datum wählen',
      availableSlots: 'Verfügbare Termine',
      noSlots: 'Keine Termine für diesen Tag verfügbar.',
      noBarbers: 'Keine Barbier für diesen Shop gefunden.',
      finalDetails: 'Letzte Details',
      yourInformation: 'Ihre Informationen',
      provideDetails: 'Bitte geben Sie Ihre Kontaktdaten für die Buchung an.',
      fullName: 'Vollständiger Name',
      emailAddress: 'E-Mail-Adresse',
      phoneNumber: 'Telefonnummer',
      firstName: 'Vorname',
      lastName: 'Nachname',
      companyName: 'Firmenname (Barbershop-Name)',
      address: 'Adresse',
      reviewBooking: 'Buchung überprüfen',
      changeTime: 'Zeit ändern',
      confirmYourBooking: 'Buchung bestätigen',
      reviewBeforeSubmission: 'Bitte überprüfen Sie alle Details vor der endgültigen Absendung.',
      selectedServices: 'Ausgewählte Dienstleistungen',
      shopAndTime: 'Shop & Zeit',
      customerInformation: 'Kundeninformationen',
      confirmAndBook: 'Bestätigen & Jetzt buchen',
      editDetails: 'Details bearbeiten',
      cancellationPolicy: 'Stornierungsbedingungen',
      cancellationTerms: 'Sie können Ihren Termin bis zu 24 Stunden vor der geplanten Zeit stornieren oder verschieben. Bei Stornierungen innerhalb von 24 Stunden fällt eine Gebühr von 50% an.',
      acceptPolicy: 'Ich stimme den Stornierungsbedingungen zu',
      youAreSharp: 'Du bist scharf!',
      confirmationSent: 'Wir haben eine Bestätigungs-E-Mail an {email} gesendet.',
      seeYouAt: 'Wir sehen uns bei {shop} am {date} um {time}!',
      bookAnother: 'Weitere buchen',
      yourBookings: 'Ihre Buchungen',
      manageAppointments: 'Sehen und verwalten Sie Ihre anstehenden Termine.',
      noBookings: 'Keine Buchungen gefunden.',
      cancelBooking: 'Buchung stornieren',
      cancelConfirm: 'Sind Sie sicher, dass Sie diese Buchung stornieren möchten?',
      manageBookingsPrompt: 'Möchten Sie Ihre Buchungen einfach verwalten?',
      loginOrSignUp: 'Anmelden oder Registrieren',
      continueAsGuest: 'Als Gast fortfahren',
      agreeToTerms: 'Ich stimme den {terms} und der {privacy} zu',
      mustAgree: 'Sie müssen den Allgemeinen Geschäftsbedingungen zustimmen, um fortzufahren',
      bookSlot: 'Termin buchen',
      rateExperience: 'Bewerten Sie Ihre Erfahrung',
      shareFeedback: 'Teilen Sie uns Ihr Feedback mit...',
      leaveReview: 'Bewertung hinterlassen',
      reviewSubmitted: 'Bewertung erfolgreich abgeschickt',
      excellent: 'Ausgezeichnet',
      veryGood: 'Sehr gut',
      good: 'Gut',
      fair: 'Mittelmässig',
      poor: 'Schlecht',
    },
    pricing: {
      title: 'Steigern Sie Ihr Barbiere-Geschäft',
      subtitle: 'Treten Sie dem Elite-Netzwerk von Barbershops bei, die unser Premium-Managementsystem nutzen. Optimieren Sie Ihre Buchungen, verwalten Sie Ihr Personal und lassen Sie Ihre Marke wachsen.',
      proPlan: 'Pro Barbershop',
      monthlySub: 'Monatliches Abonnement',
      getStarted: 'Jetzt loslegen',
      cancelAnytime: 'Jederzeit kündbar • Sichere Zahlung über Stripe',
      features: {
        unlimitedBarbers: 'Unbegrenzte Barbiere',
        unlimitedBookings: 'Unbegrenzte Buchungen',
        customServices: 'Eigene Services & Kategorien',
        realtimeDash: 'Echtzeit-Dashboard',
        emailNotifications: 'E-Mail-Benachrichtigungen',
        multiLanguage: 'Mehrsprachige Unterstützung',
        prioritySupport: 'Prioritärer Support',
        advancedAnalytics: 'Erweiterte Analysen',
        upTo3Barbers: 'Bis zu 3 Barbiere',
        standardAnalytics: 'Standard-Analysen',
      },
      saveTwoMonths: '2 Monate sparen',
      fourteenDayTrial: '14 Tage kostenlos testen',
      noChargeToday: 'Keine Kosten heute • Testphase beginnt jetzt',
      billedMonthly: 'Monatliche Abrechnung',
      billedAnnually: 'Jährliche Abrechnung',
      monthly: 'Monatlich',
      annually: 'Jährlich',
      basicDesc: 'Perfekt für kleine Shops am Anfang',
      proDesc: 'Die Komplettlösung für wachsende Shops',
      mostPopular: 'Am beliebtesten',
      iAgreeTo: 'ICH STIMME DEN',
      and: 'UND',
      subRequired: 'Abonnement erforderlich',
      subRequiredDesc: 'Um auf das Admin-Dashboard zuzugreifen und Ihren Shop zu verwalten, benötigen Sie ein aktives Abonnement.',
      viewPricing: 'Preise anzeigen',
      totalPerYear: 'Gesamt: {price} / Jahr',
      perMonth: 'pro Monat',
      perMonthBilledAnnually: 'pro Monat, jährlich abgerechnet',
      startTrial: '14 Tage kostenlos testen',
      authRequired: 'Authentifizierung erforderlich',
      authDesc: 'Bitte melden Sie sich an oder erstellen Sie ein Konto, um Ihr Abonnement zu starten.',
    },
    legal: {
      terms: 'Allgemeine Geschäftsbedingungen',
      privacy: 'Datenschutzerklärung',
      cookies: 'Cookie-Richtlinie',
      cookieConsent: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern. Durch den weiteren Besuch dieser Website stimmen Sie unserer Verwendung von Cookies zu.',
      accept: 'Akzeptieren',
      decline: 'Ablehnen',
    },
    admin: {
      reservations: 'Reservierungen',
      noReservations: 'Keine Reservierungen gefunden',
      customers: 'Kunden',
      slots: 'Termine',
      services: 'Dienstleistungen',
      team: 'Team',
      shops: 'Filialen',
      settings: 'Einstellungen',
      roles: 'Rollen',
      userRoles: 'Benutzerrollen',
      platformUsers: 'Plattform-Benutzer',
      systemOverview: 'Systemübersicht',
      activeSubscriptions: 'Aktive Abonnements',
      totalPlatformUsers: 'Gesamte Plattform-Benutzer',
      platformHealth: 'Systemstatus',
      platformHealthDesc: 'Ihre Plattform läuft derzeit auf optimalem Niveau. Alle Systeme sind betriebsbereit.',
      systemStable: 'System stabil',
      billingTransactions: 'Abrechnungstransaktionen',
      recentBilling: 'Aktuelle Abrechnungen',
      viewAllTransactions: 'Alle Transaktionen anzeigen',
      shopLocationUpdated: 'Shop-Standort aktualisiert',
      failedUpdateLocation: 'Fehler beim Aktualisieren des Shop-Standorts',
      enterValidCoords: 'Bitte geben Sie gültige Koordinaten ein',
      deleteShopConfirm: 'Sind Sie sicher, dass Sie diesen Shop löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      shopControl: 'Shop-Steuerung',
      superAdmin: 'Super-Admin',
      barberAdmin: 'Shop-Admin',
      barberStaff: 'Barbier-Personal',
      customerRole: 'Kunde',
      status: 'STATUS',
      myDashboard: 'MEIN DASHBOARD',
      chat: 'CHAT',
      adminDashboard: 'Admin-Dashboard',
      superAdminDashboard: 'Super-Admin-Dashboard',
      menuItems: 'Menüpunkte',
      instantSetup: 'Sofortige Einrichtung',
      generateSlots: 'Termine generieren',
      startTime: 'Startzeit',
      endTime: 'Endzeit',
      intervalMins: 'Intervall (Minuten)',
      customInterval: 'Benutzerdefiniertes Intervall',
      service: 'Dienstleistung',
      breakStart: 'Pause Start',
      breakEnd: 'Pause Ende',
      clearAvailability: 'Verfügbarkeit löschen',
      confirmClearAvailability: 'Sind Sie sicher, dass Sie alle verfügbaren Termine für diesen Tag löschen möchten?',
      slotsGenerated: 'Termine erfolgreich generiert',
      failedGenerateSlots: 'Fehler beim Generieren der Termine',
      availabilityCleared: 'Verfügbarkeit gelöscht',
      failedClearAvailability: 'Fehler beim Löschen der Verfügbarkeit',
      description: 'Dienstleistungsbeschreibung',
      addService: 'Dienstleistung hinzufügen',
      searchServices: 'Dienstleistungen suchen...',
      searchTeam: 'Team suchen...',
      searchReservations: 'Reservierungen suchen...',
      searchShops: 'Shops suchen...',
      searchUsers: 'Benutzer suchen...',
      searchTransactions: 'Transaktionen suchen...',
      totalBookings: 'Gesamtbuchungen',
      lastBooking: 'Letzte Buchung',
      recentReservations: 'Aktuelle Reservierungen',
      customer: 'Kunde',
      dateAndTime: 'Datum & Zeit',
      addSlot: 'Termin hinzufügen',
      manageStaff: 'Personal und Administratorrollen verwalten',
      allRoles: 'Alle Rollen',
      allCategories: 'Alle Kategorien',
      allBarbers: 'Alle Barbiere',
      shop: 'Shop',
      allShops: 'Alle Shops (Übersicht)',
      signOut: 'Abmelden',
      profileNotFound: 'Profil nicht gefunden',
      accessDenied: 'Zugriff verweigert',
      selectCategory: 'Kategorie wählen',
      haircut: 'Haarschnitt',
      shave: 'Rasur',
      styling: 'Styling',
      treatment: 'Behandlung',
      joined: 'Beigetreten',
      currentRole: 'Aktuelle Rolle',
      barberShop: 'Barbershop',
      actions: 'Aktionen',
      makeSuperAdmin: 'Zum Super Admin machen',
      makeAdmin: 'Zum Shop-Admin machen',
      makeStaff: 'Zum Barbier-Personal machen',
      revoke: 'Widerrufen',
      noShopAssigned: 'Kein Shop zugewiesen',
      noShop: 'Kein Shop',
      updateRole: 'Benutzerrolle aktualisieren',
      confirmUpdateRole: 'Sind Sie sicher, dass Sie die Rolle dieses Benutzers ändern möchten? Dies kann den Zugriff auf administrative Funktionen gewähren oder einschränken.',
      currentUser: 'Aktueller Benutzer',
      manageSystemRoles: 'Systemweite Benutzerberechtigungen verwalten',
      general: 'Allgemein',
      hours: 'Öffnungszeiten',
      shopStatus: 'Shop-Status',
      activateDeactivateShop: 'Diesen Shop aktivieren oder deaktivieren',
      manageShopDetails: 'Verwalten Sie Ihre Barbershop-Details',
      shopName: 'Shop-Name',
      address: 'Adresse',
      openingTime: 'Öffnungszeit',
      closingTime: 'Schließzeit',
      appointmentReminders: 'Terminerinnerungen',
      enableReminders: 'Erinnerungen aktivieren',
      enableRemindersDesc: 'Automatisierte Erinnerungen an Kunden senden',
      reminderType: 'Erinnerungstyp',
      emailOnly: 'Nur E-Mail',
      smsOnly: 'Nur SMS',
      bothEmailSms: 'E-Mail & SMS',
      reminderTypeDesc: 'Wählen Sie aus, wie Kunden ihre Terminerinnerungen erhalten',
      hoursBefore: 'Stunden vorher',
      sendingReminders: 'Erinnerungen werden gesendet...',
      sendRemindersManual: 'Erinnerungen jetzt senden (manueller Auslöser)',
      longitude: 'Längengrad',
      latitude: 'Breitengrad',
      updateShopDetails: 'Shop-Details aktualisieren',
      onlyAdminsModify: 'Nur Shop-Administratoren können diese Einstellungen ändern.',
      noShopSelected: 'Kein Shop ausgewählt oder zugewiesen.',
      user: 'Benutzer',
      category: 'Kategorie',
      min: 'Minuten',
      photoUrl: 'Foto',
      price: 'Preis',
      bio: 'Biografie',
      linkUser: 'Mit Benutzerkonto verknüpfen',
      noUser: 'Kein Benutzer verknüpft',
      addBarber: 'Barbier hinzufügen',
      editBarber: 'Barbier bearbeiten',
      editLocation: 'Standort bearbeiten',
      barberAdded: 'Barbier erfolgreich hinzugefügt',
      barberUpdated: 'Barbier erfolgreich aktualisiert',
      barberDeleted: 'Barbier erfolgreich gelöscht',
      linkedBarber: 'Verknüpfter Barbier',
      break: 'Pausenzeiten',
      changePhoto: 'Foto ändern',
      uploadPhoto: 'Foto hochladen',
      photoUploadHint: 'Empfohlen: Quadratisches Format, max. 2 MB',
      failedAddBarber: 'Fehler beim Hinzufügen des Barbiers',
      failedUpdateBarber: 'Fehler beim Aktualisieren des Barbiers',
      failedDeleteBarber: 'Fehler beim Löschen des Barbiers',
      deleteBarber: 'Barbier löschen',
      confirmDeleteBarber: 'Sind Sie sicher, dass Sie diesen Barbier löschen möchten?',
      deleteService: 'Service löschen',
      confirmDeleteService: 'Sind Sie sicher, dass Sie diese Dienstleistung löschen möchten?',
      deleteSlot: 'Slot löschen',
      confirmDeleteSlot: 'Sind Sie sicher, dass Sie dieses Zeitfenster löschen möchten?',
      cancelReservation: 'Reservierung stornieren',
      confirmCancelReservation: 'Sind Sie sicher, dass Sie diese Reservierung stornieren möchten?',
      roleUpdated: 'Rolle auf {role} aktualisiert',
      reservationStatusUpdated: 'Reservierungsstatus auf {status} aktualisiert',
      reservationUpdated: 'Reservierung erfolgreich aktualisiert',
      failedUpdateReservation: 'Fehler beim Aktualisieren der Reservierung',
      editReservation: 'Reservierung bearbeiten',
      customerDetails: 'Kundendetails',
      newReservation: 'Neue Reservierung',
      newBookingMade: 'Eine neue Buchung wurde vorgenommen.',
      failedUpdateRole: 'Rolle konnte nicht aktualisiert werden',
      categories: 'Kategorien',
      addCategory: 'Kategorie hinzufügen',
      categoryExists: 'Kategorie existiert bereits',
      categoryAdded: 'Kategorie erfolgreich hinzugefügt',
      categoryDeleted: 'Kategorie erfolgreich gelöscht',
      categoryUpdated: 'Kategorie erfolgreich aktualisiert',
      failedUpdateCategory: 'Kategorie konnte nicht aktualisiert werden',
      categoryInUse: 'Kategorie kann nicht gelöscht werden, da sie verwendet wird',
      addNewCategory: 'Neue Kategorie hinzufügen...',
      selectShopFirst: 'Bitte wählen Sie zuerst einen Shop aus',
      slotExists: 'Dieses Zeitfenster existiert bereits',
      slotAdded: 'Termin erfolgreich hinzugefügt',
      failedAddSlot: 'Termin konnte nicht hinzugefügt werden',
      slotDeleted: 'Termin erfolgreich gelöscht',
      failedDeleteSlot: 'Termin konnte nicht gelöscht werden',
      failedUpdateStatus: 'Status konnte nicht aktualisiert werden',
      selectBarberFirst: 'Bitte wählen Sie zuerst einen Barbier aus',
      invalidPriceDuration: 'Ungültiger Preis oder Dauer',
      serviceAdded: 'Dienstleistung erfolgreich hinzugefügt',
      serviceUpdated: 'Dienstleistung erfolgreich aktualisiert',
      failedAddService: 'Dienstleistung konnte nicht hinzugefügt werden',
      failedUpdateService: 'Dienstleistung konnte nicht aktualisiert werden',
      serviceDeleted: 'Dienstleistung erfolgreich gelöscht',
      failedDeleteService: 'Dienstleistung konnte nicht gelöscht werden',
      shopNameRequired: 'Shop-Name ist erforderlich',
      invalidClosingTime: 'Die Schließzeit muss nach der Öffnungszeit liegen',
      shopAdded: 'Shop erfolgreich hinzugefügt',
      failedAddShop: 'Shop konnte nicht hinzugefügt werden',
      confirmDeleteShop: 'Sind Sie sicher, dass Sie diesen Shop löschen möchten?',
      shopDeleted: 'Shop erfolgreich gelöscht',
      failedDeleteShop: 'Shop konnte nicht gelöscht werden',
      deleteShop: 'Shop löschen',
      shopUpdated: 'Shop erfolgreich aktualisiert',
      failedUpdateShop: 'Shop konnte nicht aktualisiert werden',
      remindersNotEnabled: 'Erinnerungen sind für diesen Shop nicht aktiviert',
      failedSendReminders: 'Erinnerungen konnten nicht gesendet werden',
      reminderSent: 'Erinnerung erfolgreich gesendet',
      remindersSent: '{count} Erinnerungen erfolgreich gesendet',
      reminder: 'Erinnerung',
      sent: 'Gesendet',
      notSent: 'Nicht gesendet',
      sendReminder: 'Erinnerung senden',
      profileNotFoundDesc: 'Ihr Benutzerprofil konnte nicht gefunden werden.',
      accessDeniedDesc: 'Sie haben keine Berechtigung, auf diese Seite zuzugreifen.',
      control: 'Steuerung',
      allStatus: 'Alle Status',
      dateTime: 'Datum & Zeit',
      addSlots: 'Termine hinzufügen',
      timeSlot: 'Zeitfenster',
      availability: 'Verfügbarkeit',
      manageStaffAvailability: 'Personal- und Shop-Verfügbarkeit verwalten',
      slotsForDay: 'Termine für den ausgewählten Tag',
      for: 'für',
      showingSlotsFor: 'Termine anzeigen für',
      noSlotsAdded: 'Keine Termine für diesen Tag hinzugefügt',
      addFirst: 'Termine hinzufügen',
      availableSlots: 'Verfügbare Termine',
      teamManagement: 'Team-Management',
      manageStaffRoles: 'Personal und Administratorrollen verwalten',
      addStaff: 'Personal-Konto hinzufügen',
      staffAccountCreated: 'Personal-Konto erfolgreich erstellt',
      failedCreateStaff: 'Personal-Konto konnte nicht erstellt werden',
      staffCreationNotice: 'Dies erstellt ein neues Login-Konto für Ihr Teammitglied. Es wird automatisch Ihrem Salon zugewiesen.',
      createAccount: 'Konto erstellen',
      instructions: 'Anweisungen',
      teamInstructions: 'Verwalten Sie Ihre Teammitglieder und weisen Sie sie bestimmten Shops zu. Admins können Dienstleistungen und Termine für ihren zugewiesenen Shop verwalten.',
      menu: 'Menü',
      items: 'Artikel',
      subscription: 'Abonnement',
      currentPlan: 'Aktueller Plan',
      renewsOn: 'Erneuert am',
      manageSubscription: 'Abonnement verwalten',
      trialEndsOn: 'Testphase endet am',
      serviceName: 'Dienstleistungsname',
      opening: 'Öffnung',
      closing: 'Schließung',
      barber: 'Barbier',
      inventory: 'Inventar',
      marketing: 'Marketing',
      analytics: 'Analyse',
      commissions: 'Provisionen',
      stock: 'Lagerbestand',
      minStock: 'Mindestbestand',
      quantity: 'Menge',
      unit: 'Einheit',
      addPromo: 'Gutscheincode hinzufügen',
      code: 'Code',
      discount: 'Rabatt',
      expires: 'Ablaufdatum',
      active: 'Aktiv',
      revenue: 'Umsatz',
      growth: 'Wachstum',
      monthlyGrowth: 'Monatliches Wachstum',
      topServices: 'Top-Dienstleistungen',
      topBarbers: 'Top-Barbiere',
      totalRevenue: 'Gesamtumsatz',
      avgRating: 'Durchschn. Bewertung',
      activeCustomers: 'Aktive Kunden',
      revenueLast30Days: 'Umsatz (Letzte 30 Tage)',
      bookingsByDay: 'Buchungen nach Tag',
      popularServices: 'Beliebte Dienste',
      revenueByBarber: 'Umsatz nach Barbier',
      commissionRate: 'Provisionssatz',
      earned: 'Verdient',
      paid: 'Bezahlt',
      generateForDays: 'Tage zum Generieren',
      skipWeekends: 'Wochenenden überspringen',
      bulkCreateAvailability: 'Verfügbarkeit in großen Mengen erstellen',
      workingDays: 'Arbeitstage',
      startDate: 'Startdatum',
      endDate: 'Enddatum',
      quickTips: 'Schnelle Tipps',
      tip1: 'Wählen Sie zuerst einen Barbier aus, um dessen spezifische Verfügbarkeit zu verwalten.',
      tip2: 'Verwenden Sie den Bulk-Generator, um schnell Termine für einen ganzen Monat einzurichten.',
      tip3: 'Klicken Sie auf einen gebuchten Termin, um die Details anzuzeigen.',
      noSlotsShort: 'Keine',
      addItem: 'Artikel hinzufügen',
      addShop: 'Shop hinzufügen',
      automatedCampaigns: 'Automatisierte Kampagnen',
      campaignsDesc: 'Automatische Marketingnachrichten einrichten',
      promoCodes: 'Gutscheincodes',
      lowStock: 'Niedriger Bestand',
      inStock: 'Auf Lager',
      item: 'Artikel',
      amount: 'Betrag',
    },
    home: {
      heroTitle: 'Lebendiges Barbiere-Erlebnis',
      heroSubtitle: 'Buchen Sie Ihren nächsten Schnitt bei den Besten der Stadt. Professioneller Service, moderner Stil.',
      bookNow: 'Jetzt buchen',
      ourServices: 'Unsere Dienstleistungen',
      contactUs: 'Kontaktieren Sie uns',
    }
  },
  fr: {
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      search: 'Rechercher',
      loading: 'Chargement...',
      all: 'Tout',
      confirm: 'Confirmer',
      pending: 'En attente',
      cancelled: 'Annulé',
      confirmed: 'Confirmé',
      back: 'Retour',
      continue: 'Continuer',
      processing: 'Traitement...',
      saving: 'Enregistrement...',
      total: 'Total',
      status: 'Statut',
      actions: 'Actions',
      view: 'Voir',
      edit: 'Modifier',
      name: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      address: 'Adresse',
      date: 'Date',
      time: 'Heure',
      price: 'Prix',
      duration: 'Durée',
      category: 'Catégorie',
      description: 'Description',
      available: 'Disponible',
      booked: 'Réservé',
      sharp: 'Sharp',
      cuts: 'Cuts',
      est: 'Est. 2024 • Coiffure Premium',
      currency: 'CHF',
      none: 'Aucun',
      selectLanguage: 'Choisir la langue',
      na: 'N/D',
      noAddress: 'Aucune adresse fournie',
      today: "Aujourd'hui",
      min: 'Minutes',
      any: 'N\'importe quel',
      away: 'à proximité',
      customer: 'Client',
      copyright: '© {year} Sharp Cuts. Tous droits réservés.',
      terms: 'Conditions Générales',
      privacy: 'Confidentialité',
      cookies: 'Politique de Cookies',
      swissLaw: 'Conforme au droit suisse',
      validation: {
        required: 'Ce champ est obligatoire',
        email: 'Adresse e-mail invalide',
        phone: 'Numéro de téléphone suisse invalide',
        passwordMin: 'Le mot de passe doit contenir au moins 8 caractères',
        passwordMatch: 'Les mots de passe ne correspondent pas',
        nameMin: 'Le nombre de caractères doit être d\'au moins 2'
      },
      days: {
        sun: 'Dim',
        mon: 'Lun',
        tue: 'Mar',
        wed: 'Mer',
        thu: 'Jeu',
        fri: 'Ven',
        sat: 'Sam'
      }
    },
    setup: {
      required: 'Configuration requise',
      dbError: 'Erreur de base de données',
      configureSupabase: 'Veuillez configurer vos identifiants Supabase dans le fichier .env pour démarrer le système de barbier.',
      requiredVariables: 'Variables requises :',
      nextSteps: 'Étapes suivantes :',
      openDashboard: 'Ouvrez votre tableau de bord Supabase',
      goSqlEditor: 'Allez dans l\'éditeur SQL',
      runSchema: 'Copiez et exécutez le contenu de supabase_schema.sql',
      refreshPage: 'Actualisez cette page',
      dbSchemaNotFound: 'Schéma de base de données introuvable. Veuillez exécuter le schéma SQL dans votre tableau de bord Supabase.',
    },
    nav: {
      home: 'Accueil',
      dashboard: 'Tableau de bord',
      logout: 'Déconnexion',
      login: 'Connexion',
      register: 'S\'inscrire',
      booking: 'Réservation',
      book: 'Réserver',
      history: 'Compte',
      admin: 'Admin',
      systemAdmin: 'Administration du Système',
      becomePartner: 'Devenir Partenaire',
      switchLight: 'Passer au mode clair',
      switchDark: 'Passer au mode sombre',
    },
    auth: {
      signIn: 'Se connecter',
      signUp: 'S\'inscrire',
      createAccount: 'Créer un compte',
      alreadyHaveAccount: 'Vous avez déjà un compte ? Se connecter',
      dontHaveAccount: 'Vous n\'avez pas de compte ? S\'inscrire',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      passwordsDoNotMatch: 'Les mots de passe ne correspondent pas',
      verifyEmail: 'Vérifiez votre e-mail',
      checkEmail: 'Nous avons envoyé un lien sécurisé à votre e-mail. Veuillez cliquer sur le lien pour activer votre compte.',
      didntReceive: 'Pas reçu ?',
      resendEmail: 'Renvoyer l\'e-mail de confirmation',
      resendIn: 'Renvoyer dans {seconds}s',
      backToSignIn: 'Retour à la connexion',
      checkSpam: 'Vérifiez votre dossier de spam ou de courrier indésirable',
      waitMinutes: 'Attendez quelques minutes pour la livraison',
      ensureEmail: 'Assurez-vous que l\'adresse e-mail est correcte',
      adminAccess: 'Accès Admin',
      joinCrew: 'Rejoindre l\'équipe',
      signInToManage: 'Connectez-vous pour gérer votre boutique',
      customerLogin: 'Connexion client',
      signUpToBook: 'Créez un compte pour gérer vos réservations',
      signInToBook: 'Connectez-vous pour gérer vos réservations',
      strengthVeryWeak: 'Très faible',
      strengthWeak: 'Faible',
      strengthMedium: 'Moyen',
      strengthStrong: 'Fort',
      strengthVeryStrong: 'Très fort',
      forgotPassword: 'Mot de passe oublié ?',
      resetPassword: 'Réinitialiser le mot de passe',
      sendResetLink: 'Envoyer le lien de réinitialisation',
      resetLinkSent: 'Lien de réinitialisation du mot de passe envoyé ! Veuillez consulter vos e-mails.',
      newPassword: 'Nouveau mot de passe',
      updatePassword: 'Mettre à jour le mot de passe',
      passwordUpdated: 'Mot de passe mis à jour avec succès !',
      selectShopToAuth: 'Veuillez sélectionner un salon pour continuer',
      authFor: 'Authentification pour {shop}',
      emailConfirmed: 'E-mail confirmé avec succès ! Vous pouvez maintenant vous connecter.',
    },
    booking: {
      bookNow: 'Réserver maintenant',
      myBookings: 'Mes réservations',
      chooseServices: 'Choisir les services',
      pickTime: 'Choisir une heure',
      yourDetails: 'Vos détails',
      confirmBooking: 'Confirmer la réservation',
      stepOf: 'Étape {step} sur {total}',
      selectShop: 'Choisir un salon',
      selectBarber: 'Sélectionnez un barbier',
      chooseLocation: 'Choisissez votre emplacement préféré pour prendre rendez-vous.',
      chooseBarber: 'Choisissez votre barbier préféré pour votre rendez-vous.',
      changeShop: 'Changer de boutique',
      changeBarber: 'Changer de barbier',
      totalSelection: 'Sélection totale',
      selectDate: 'Sélectionner une date',
      availableSlots: 'Créneaux disponibles',
      noSlots: 'Aucun créneau disponible pour ce jour.',
      noBarbers: 'Aucun barbier trouvé pour ce salon.',
      finalDetails: 'Détails finaux',
      yourInformation: 'Vos informations',
      provideDetails: 'Veuillez fournir vos coordonnées pour la réservation.',
      fullName: 'Nom complet',
      emailAddress: 'Adresse e-mail',
      phoneNumber: 'Numéro de téléphone',
      firstName: 'Prénom',
      lastName: 'Nom de famille',
      companyName: 'Nom de l\'entreprise (Nom du salon)',
      address: 'Adresse',
      reviewBooking: 'Vérifier la réservation',
      changeTime: 'Changer l\'heure',
      confirmYourBooking: 'Confirmez votre réservation',
      reviewBeforeSubmission: 'Veuillez vérifier tous les détails avant la soumission finale.',
      selectedServices: 'Services sélectionnés',
      shopAndTime: 'Boutique et heure',
      customerInformation: 'Informations client',
      confirmAndBook: 'Confirmer et réserver maintenant',
      editDetails: 'Modifier les détails',
      cancellationPolicy: 'Politique d\'annulation',
      cancellationTerms: 'Vous pouvez annuler ou reporter votre rendez-vous jusqu\'à 24 heures avant l\'heure prévue. Les annulations effectuées dans les 24 heures entraîneront des frais de 50 %.',
      acceptPolicy: 'J\'accepte la politique d\'annulation',
      youAreSharp: 'Vous êtes au top !',
      confirmationSent: 'Nous avons envoyé un e-mail de confirmation à {email}.',
      seeYouAt: 'Rendez-vous chez {shop} le {date} à {time} !',
      bookAnother: 'Réserver un autre',
      yourBookings: 'Vos réservations',
      manageAppointments: 'Consultez et gérez vos rendez-vous à venir.',
      noBookings: 'Aucune réservation trouvée.',
      cancelBooking: 'Annuler la réservation',
      cancelConfirm: 'Êtes-vous sûr de vouloir annuler cette réservation ?',
      manageBookingsPrompt: 'Vous souhaitez gérer vos réservations facilement ?',
      loginOrSignUp: 'Se connecter ou S\'inscrire',
      continueAsGuest: 'Continuer en tant qu\'invité',
      agreeToTerms: 'J\'accepte les {terms} et la {privacy}',
      mustAgree: 'Vous devez accepter les conditions générales pour continuer',
      bookSlot: 'Réserver un créneau',
      rateExperience: 'Évaluez votre expérience',
      shareFeedback: 'Partagez vos commentaires avec nous...',
      leaveReview: 'Laisser un avis',
      reviewSubmitted: 'Avis soumis avec succès',
      excellent: 'Excellent',
      veryGood: 'Très bien',
      good: 'Bien',
      fair: 'Passable',
      poor: 'Médiocre',
    },
    pricing: {
      title: 'Élevez votre entreprise de barbier',
      subtitle: 'Rejoignez le réseau d\'élite des salons de coiffure utilisant notre système de gestion premium. Simplifiez vos réservations, gérez votre personnel et développez votre marque.',
      proPlan: 'Salon de Coiffure Pro',
      monthlySub: 'Abonnement Mensuel',
      getStarted: 'Commencer maintenant',
      cancelAnytime: 'Annulez à tout moment • Paiement sécurisé via Stripe',
      features: {
        unlimitedBarbers: 'Barbiers illimités',
        unlimitedBookings: 'Réservations illimitées',
        customServices: 'Services et catégories personnalisés',
        realtimeDash: 'Tableau de bord en temps réel',
        emailNotifications: 'Notifications par e-mail',
        multiLanguage: 'Support multilingue',
        prioritySupport: 'Support prioritaire',
        advancedAnalytics: 'Analyses avancées',
        upTo3Barbers: 'Jusqu\'à 3 barbiers',
        standardAnalytics: 'Analyses standard',
      },
      saveTwoMonths: 'Économisez 2 mois',
      fourteenDayTrial: 'Essai gratuit de 14 jours',
      noChargeToday: 'Aucun frais aujourd\'hui • L\'essai commence maintenant',
      billedMonthly: 'Facturé mensuellement',
      billedAnnually: 'Facturé annuellement',
      monthly: 'Mensuel',
      annually: 'Annuel',
      basicDesc: 'Parfait pour les petits salons débutants',
      proDesc: 'La solution complète pour les salons en pleine croissance',
      mostPopular: 'Le plus populaire',
      iAgreeTo: 'J\'ACCEPTE LES',
      and: 'ET',
      subRequired: 'Abonnement Requis',
      subRequiredDesc: 'Pour accéder au tableau de bord administrateur et gérer votre salon, vous avez besoin d\'un abonnement actif.',
      viewPricing: 'Voir les Prix',
      totalPerYear: 'Total : {price} / an',
      perMonth: 'par mois',
      perMonthBilledAnnually: 'par mois, facturé annuellement',
      startTrial: 'Commencer l\'essai de 14 jours',
      authRequired: 'Authentification requise',
      authDesc: 'Veuillez vous connecter ou créer un compte pour commencer votre abonnement.',
    },
    legal: {
      terms: 'Conditions Générales',
      privacy: 'Politique de Confidentialité',
      cookies: 'Politique relative aux cookies',
      cookieConsent: 'Nous utilisons des cookies pour améliorer votre expérience. En continuant à visiter ce site, vous acceptez notre utilisation des cookies.',
      accept: 'Accepter',
      decline: 'Refuser',
    },
    admin: {
      reservations: 'Réservations',
      noReservations: 'Aucune réservation trouvée',
      customers: 'Clients',
      slots: 'Créneaux',
      services: 'Services',
      team: 'Équipe',
      shops: 'Boutiques',
      settings: 'Paramètres',
      roles: 'Rôles',
      userRoles: 'Rôles des utilisateurs',
      platformUsers: 'Utilisateurs de la Plateforme',
      systemOverview: 'Aperçu du Système',
      activeSubscriptions: 'Abonnements Actifs',
      totalPlatformUsers: 'Total Utilisateurs',
      platformHealth: 'Santé de la Plateforme',
      platformHealthDesc: 'Votre plateforme fonctionne actuellement à des niveaux optimaux. Tous les systèmes sont opérationnels.',
      systemStable: 'Système Stable',
      billingTransactions: 'Transactions de Facturation',
      recentBilling: 'Facturation Récente',
      viewAllTransactions: 'Voir Toutes les Transactions',
      shopLocationUpdated: 'Emplacement du salon mis à jour',
      failedUpdateLocation: 'Échec de la mise à jour de l\'emplacement',
      enterValidCoords: 'Veuillez saisir des coordonnées valides',
      deleteShopConfirm: 'Êtes-vous sûr de vouloir supprimer ce salon ? Cette action est irréversible.',
      shopControl: 'Contrôle de la boutique',
      superAdmin: 'Super Admin',
      barberAdmin: 'Admin de Salon',
      barberStaff: 'Personnel Barbier',
      customerRole: 'Client',
      status: 'STATUT',
      myDashboard: 'MON TABLEAU DE BORD',
      chat: 'CHAT',
      adminDashboard: 'Tableau de bord Admin',
      superAdminDashboard: 'Tableau de bord Super Admin',
      menuItems: 'Articles du menu',
      instantSetup: 'Configuration instantanée',
      generateSlots: 'Générer des créneaux',
      startTime: 'Heure de début',
      endTime: 'Heure de fin',
      intervalMins: 'Intervalle (mins)',
      customInterval: 'Intervalle personnalisé',
      service: 'Service',
      breakStart: 'Début de pause',
      breakEnd: 'Fin de pause',
      clearAvailability: 'Effacer la disponibilité',
      confirmClearAvailability: 'Êtes-vous sûr de vouloir supprimer tous les créneaux disponibles pour ce jour ?',
      slotsGenerated: 'Créneaux générés avec succès',
      failedGenerateSlots: 'Échec de la génération des créneaux',
      availabilityCleared: 'Disponibilité effacée',
      failedClearAvailability: 'Échec de l\'effacement de la disponibilité',
      description: 'Description du service',
      addService: 'Ajouter un service',
      searchServices: 'Rechercher des services...',
      searchTeam: 'Rechercher une équipe...',
      searchReservations: 'Rechercher des réservations...',
      searchShops: 'Rechercher des salons...',
      searchUsers: 'Rechercher des utilisateurs...',
      searchTransactions: 'Rechercher des transactions...',
      totalBookings: 'Total des réservations',
      lastBooking: 'Dernière réservation',
      recentReservations: 'Réservations récentes',
      customer: 'Client',
      dateAndTime: 'Date et heure',
      addSlot: 'Ajouter un créneau',
      manageStaff: 'Gérer le personnel et les rôles d\'administrateur',
      allRoles: 'Tous les rôles',
      allCategories: 'Toutes les catégories',
      allBarbers: 'Tous les barbiers',
      shop: 'Boutique',
      allShops: 'Toutes les boutiques (Aperçu)',
      signOut: 'Déconnexion',
      profileNotFound: 'Profil non trouvé',
      accessDenied: 'Accès refusé',
      selectCategory: 'Choisir une catégorie',
      haircut: 'Coupe de cheveux',
      shave: 'Rasage',
      styling: 'Coiffage',
      treatment: 'Traitement',
      joined: 'Inscrit le',
      currentRole: 'Rôle actuel',
      barberShop: 'Salon de coiffure',
      actions: 'Actions',
      makeSuperAdmin: 'Rendre Super Admin',
      makeAdmin: 'Rendre Admin de Salon',
      makeStaff: 'Rendre Personnel Barbier',
      revoke: 'Révoquer',
      noShopAssigned: 'Aucun salon assigné',
      noShop: 'Aucun salon',
      updateRole: 'Mettre à jour le rôle',
      confirmUpdateRole: 'Êtes-vous sûr de vouloir changer le rôle de cet utilisateur ? Cela peut accorder ou restreindre l\'accès aux fonctionnalités administratives.',
      currentUser: 'Utilisateur actuel',
      manageSystemRoles: 'Gérer les permissions utilisateur du système',
      general: 'Général',
      hours: 'Horaires',
      shopStatus: 'Statut de la boutique',
      activateDeactivateShop: 'Activer ou désactiver cette boutique',
      manageShopDetails: 'Gérer les détails de votre salon de coiffure',
      shopName: 'Nom du salon',
      address: 'Adresse',
      openingTime: 'Heure d\'ouverture',
      closingTime: 'Heure de fermeture',
      appointmentReminders: 'Rappels de rendez-vous',
      enableReminders: 'Activer les rappels',
      enableRemindersDesc: 'Envoyer des rappels automatisés aux clients',
      reminderType: 'Type de rappel',
      emailOnly: 'E-mail uniquement',
      smsOnly: 'SMS uniquement',
      bothEmailSms: 'E-mail et SMS',
      reminderTypeDesc: 'Choisissez comment les clients reçoivent leurs rappels de rendez-vous',
      hoursBefore: 'Heures avant',
      sendingReminders: 'Envoi des rappels...',
      sendRemindersManual: 'Envoyer les rappels maintenant (déclenchement manuel)',
      longitude: 'Longitude',
      latitude: 'Latitude',
      updateShopDetails: 'Mettre à jour les détails du salon',
      onlyAdminsModify: 'Seuls les administrateurs du salon peuvent modifier ces paramètres.',
      noShopSelected: 'Aucun salon sélectionné ou assigné.',
      user: 'Utilisateur',
      category: 'Catégorie',
      min: 'Minutes',
      photoUrl: 'Photo',
      price: 'Prix',
      bio: 'Biographie',
      linkUser: 'Lier à un compte utilisateur',
      noUser: 'Aucun utilisateur lié',
      addBarber: 'Ajouter un barbier',
      editBarber: 'Modifier le barbier',
      editLocation: 'Modifier l\'emplacement',
      barberAdded: 'Barbier ajouté avec succès',
      barberUpdated: 'Barbier mis à jour avec succès',
      barberDeleted: 'Barbier supprimé avec succès',
      linkedBarber: 'Barbier lié',
      break: 'Temps de pause',
      changePhoto: 'Modifier la photo',
      uploadPhoto: 'Charger une photo',
      photoUploadHint: 'Recommandé : Format carré, max 2 Mo',
      failedAddBarber: 'Échec de l\'ajout du barbier',
      failedUpdateBarber: 'Échec de la mise à jour du barbier',
      failedDeleteBarber: 'Échec de la suppression du barbier',
      deleteBarber: 'Supprimer le barbier',
      confirmDeleteBarber: 'Êtes-vous sûr de vouloir supprimer ce barbier ?',
      deleteService: 'Supprimer le service',
      confirmDeleteService: 'Êtes-vous sûr de vouloir supprimer ce service ?',
      deleteSlot: 'Supprimer le créneau',
      confirmDeleteSlot: 'Êtes-vous sûr de vouloir supprimer ce créneau horaire ?',
      cancelReservation: 'Annuler la réservation',
      confirmCancelReservation: 'Êtes-vous sûr de vouloir annuler cette réservation ?',
      roleUpdated: 'Rôle mis à jour en {role}',
      reservationStatusUpdated: 'Statut de la réservation mis à jour en {status}',
      reservationUpdated: 'Réservation mise à jour avec succès',
      failedUpdateReservation: 'Échec de la mise à jour de la réservation',
      editReservation: 'Modifier la réservation',
      customerDetails: 'Détails du client',
      newReservation: 'Nouvelle réservation',
      newBookingMade: 'Une nouvelle réservation a été effectuée.',
      failedUpdateRole: 'Échec de la mise à jour du rôle',
      categories: 'Catégories',
      addCategory: 'Ajouter une catégorie',
      categoryExists: 'La catégorie existe déjà',
      categoryAdded: 'Catégorie ajoutée avec succès',
      categoryDeleted: 'Catégorie supprimée avec succès',
      categoryUpdated: 'Catégorie mise à jour avec succès',
      failedUpdateCategory: 'Échec de la mise à jour de la catégorie',
      categoryInUse: 'Impossible de supprimer une catégorie en cours d\'utilisation',
      addNewCategory: 'Ajouter une nouvelle catégorie...',
      selectShopFirst: 'Veuillez d\'abord sélectionner une boutique',
      slotExists: 'Ce créneau horaire existe déjà',
      slotAdded: 'Créneau ajouté avec succès',
      failedAddSlot: 'Échec de l\'ajout du créneau',
      slotDeleted: 'Créneau supprimé avec succès',
      failedDeleteSlot: 'Échec de la suppression du créneau',
      failedUpdateStatus: 'Échec de la mise à jour du statut',
      selectBarberFirst: 'Veuillez d\'abord sélectionner un barbier',
      invalidPriceDuration: 'Prix ou durée invalide',
      serviceAdded: 'Service ajouté avec succès',
      serviceUpdated: 'Service mis à jour avec succès',
      failedAddService: 'Échec de l\'ajout du service',
      failedUpdateService: 'Échec de la mise à jour du service',
      serviceDeleted: 'Service supprimé avec succès',
      failedDeleteService: 'Échec de la suppression du service',
      shopNameRequired: 'Le nom de la boutique est requis',
      invalidClosingTime: 'L\'heure de fermeture doit être après l\'heure d\'ouverture',
      shopAdded: 'Boutique ajoutée avec succès',
      failedAddShop: 'Échec de l\'ajout de la boutique',
      confirmDeleteShop: 'Êtes-vous sûr de vouloir supprimer cette boutique ?',
      shopDeleted: 'Boutique supprimée avec succès',
      failedDeleteShop: 'Échec de la suppression de la boutique',
      deleteShop: 'Supprimer la boutique',
      shopUpdated: 'Boutique mise à jour avec succès',
      failedUpdateShop: 'Échec de la mise à jour de la boutique',
      remindersNotEnabled: 'Les rappels ne sont pas activés pour cette boutique',
      failedSendReminders: 'Échec de l\'envoi des rappels',
      reminderSent: 'Rappel envoyé avec succès',
      remindersSent: '{count} rappels envoyés avec succès',
      reminder: 'Rappel',
      sent: 'Envoyé',
      notSent: 'Non envoyé',
      sendReminder: 'Envoyer le rappel',
      profileNotFoundDesc: 'Nous n\'avons pas pu trouver votre profil utilisateur.',
      accessDeniedDesc: 'Vous n\'avez pas la permission d\'accéder à cette page.',
      control: 'Contrôle',
      allStatus: 'Tous les statuts',
      dateTime: 'Date et heure',
      addSlots: 'Ajouter des créneaux',
      timeSlot: 'Créneau horaire',
      availability: 'Disponibilité',
      manageStaffAvailability: 'Gérer la disponibilité du personnel et du salon',
      slotsForDay: 'Créneaux pour le jour sélectionné',
      for: 'pour',
      showingSlotsFor: 'Affichage des créneaux pour',
      noSlotsAdded: 'Aucun créneau ajouté pour ce jour',
      addFirst: 'Ajouter des créneaux',
      availableSlots: 'Créneaux disponibles',
      teamManagement: 'Gestion de l\'équipe',
      manageStaffRoles: 'Gérer le personnel et les rôles d\'administrateur',
      addStaff: 'Ajouter un compte personnel',
      staffAccountCreated: 'Compte personnel créé avec succès',
      failedCreateStaff: 'Échec de la création du compte personnel',
      staffCreationNotice: 'Cela créera un nouveau compte de connexion pour votre membre du personnel. Il sera automatiquement affecté à votre salon.',
      createAccount: 'Créer un compte',
      instructions: 'Instructions',
      teamInstructions: 'Gérez les membres de votre équipe et affectez-les à des boutiques spécifiques. Les administrateurs peuvent gérer les services et les créneaux pour leur boutique assignée.',
      menu: 'Menu',
      items: 'Articles',
      subscription: 'Abonnement',
      currentPlan: 'Plan actuel',
      renewsOn: 'Renouvellement le',
      manageSubscription: 'Gérer l\'abonnement',
      trialEndsOn: 'L\'essai se termine le',
      serviceName: 'Nom du service',
      opening: 'Ouverture',
      closing: 'Fermeture',
      barber: 'Barbier',
      bulkCreateAvailability: 'Créer des disponibilités en masse',
      monthlyGrowth: 'Croissance Mensuelle',
      workingDays: 'Jours de travail',
      startDate: 'Date de début',
      endDate: 'Date de fin',
      quickTips: 'Conseils rapides',
      tip1: 'Sélectionnez d\'abord un barbier pour gérer sa disponibilité spécifique.',
      tip2: 'Utilisez le générateur en masse pour configurer rapidement un mois complet de créneaux.',
      tip3: 'Cliquez sur un créneau réservé pour voir les détails du rendez-vous.',
      noSlotsShort: 'Aucun',
      addItem: 'Ajouter un article',
      addShop: 'Ajouter un magasin',
      automatedCampaigns: 'Campagnes automatisées',
      campaignsDesc: 'Configurer des messages marketing automatiques',
      promoCodes: 'Codes promo',
      addPromo: 'Ajouter une promo',
      code: 'Code',
      discount: 'Remise',
      expires: 'Expire',
      lowStock: 'Stock faible',
      inStock: 'En stock',
      item: 'Article',
      stock: 'Stock',
      minStock: 'Stock min',
      amount: 'Montant',
      commissions: 'Commissions',
    },
    home: {
      heroTitle: 'Expérience de Barbier Vibrante',
      heroSubtitle: 'Réservez votre prochaine coupe avec les meilleurs de la ville. Service professionnel, style moderne.',
      bookNow: 'Réserver maintenant',
      ourServices: 'Nos Services',
      contactUs: 'Contactez-nous',
    }
  },
  it: {
    common: {
      save: 'Salva',
      cancel: 'Annulla',
      delete: 'Elimina',
      search: 'Cerca',
      loading: 'Caricamento...',
      all: 'Tutti',
      confirm: 'Conferma',
      pending: 'In attesa',
      cancelled: 'Annullato',
      confirmed: 'Confermato',
      back: 'Indietro',
      continue: 'Continua',
      processing: 'Elaborazione...',
      saving: 'Salvataggio...',
      total: 'Totale',
      status: 'Stato',
      actions: 'Azioni',
      view: 'Visualizza',
      edit: 'Modifica',
      name: 'Nome',
      email: 'Email',
      phone: 'Telefono',
      address: 'Indirizzo',
      date: 'Data',
      time: 'Ora',
      price: 'Prezzo',
      duration: 'Durata',
      category: 'Categoria',
      description: 'Descrizione',
      available: 'Disponibile',
      booked: 'Prenotato',
      sharp: 'Sharp',
      cuts: 'Cuts',
      est: 'Dal 2024 • Barberia Premium',
      currency: 'CHF',
      none: 'Nessuno',
      selectLanguage: 'Seleziona lingua',
      na: 'N/D',
      noAddress: 'Nessun indirizzo fornito',
      barber: 'Barbiere',
      barbers: 'Barbieri',
      today: 'Oggi',
      min: 'Minuti',
      any: 'Qualsiasi',
      away: 'lontano',
      customer: 'Cliente',
      copyright: '© {year} Sharp Cuts. Tutti i diritti riservati.',
      terms: 'Termini e Condizioni',
      privacy: 'Privacy Policy',
      cookies: 'Cookie Policy',
      swissLaw: 'Conforme alla legge svizzera',
      validation: {
        required: 'Questo campo è obbligatorio',
        email: 'Indirizzo email non valido',
        phone: 'Numero di telefono svizzero non valido',
        passwordMin: 'La password deve avere almeno 8 caratteri',
        passwordMatch: 'Le password non corrispondono',
        nameMin: 'Il nome deve avere almeno 2 caratteri'
      },
      days: {
        sun: 'Dom',
        mon: 'Lun',
        tue: 'Mar',
        wed: 'Mer',
        thu: 'Gio',
        fri: 'Ven',
        sat: 'Sab'
      }
    },
    setup: {
      required: 'Configurazione richiesta',
      dbError: 'Errore del database',
      configureSupabase: 'Configura le tue credenziali Supabase nel file .env per avviare il sistema del barbiere.',
      requiredVariables: 'Variabili richieste:',
      nextSteps: 'Passaggi successivi:',
      openDashboard: 'Apri la tua dashboard Supabase',
      goSqlEditor: 'Vai all\'editor SQL',
      runSchema: 'Incolla ed esegui il contenuto di supabase_schema.sql',
      refreshPage: 'Aggiorna questa pagina',
      dbSchemaNotFound: 'Schema del database non trovato. Esegui lo schema SQL nella tua dashboard Supabase.',
    },
    nav: {
      home: 'Home',
      dashboard: 'Dashboard',
      logout: 'Esci',
      login: 'Accedi',
      register: 'Registrati',
      booking: 'Prenotazione',
      book: 'Prenota',
      history: 'Account',
      admin: 'Admin',
      systemAdmin: 'Amministrazione del Sistema',
      becomePartner: 'Diventa Partner',
      switchLight: 'Passa alla modalità chiara',
      switchDark: 'Passa alla modalità scura',
    },
    auth: {
      signIn: 'Accedi',
      signUp: 'Registrati',
      createAccount: 'Crea account',
      alreadyHaveAccount: 'Hai già un account? Accedi',
      dontHaveAccount: 'Non hai un account? Registrati',
      password: 'Password',
      confirmPassword: 'Conferma password',
      passwordsDoNotMatch: 'Le password non corrispondono',
      verifyEmail: 'Verifica la tua email',
      checkEmail: 'Abbiamo inviato un link sicuro alla tua email. Clicca sul link per attivare il tuo account.',
      didntReceive: 'Non l\'hai ricevuto?',
      resendEmail: 'Reinvia email di conferma',
      resendIn: 'Reinvia tra {seconds}s',
      backToSignIn: 'Torna all\'accesso',
      checkSpam: 'Controlla la cartella spam o posta indesiderata',
      waitMinutes: 'Attendi qualche minuto per la consegna',
      ensureEmail: 'Assicurati che l\'indirizzo email sia corretto',
      adminAccess: 'Accesso Admin',
      joinCrew: 'Unisciti al team',
      signInToManage: 'Accedi per gestire il tuo negozio',
      customerLogin: 'Accesso Clienti',
      signUpToBook: 'Crea un account per gestire le tue prenotazioni',
      signInToBook: 'Accedi per gestire le tue prenotazioni',
      strengthVeryWeak: 'Molto debole',
      strengthWeak: 'Debole',
      strengthMedium: 'Medio',
      strengthStrong: 'Forte',
      strengthVeryStrong: 'Molto forte',
      forgotPassword: 'Password dimenticata?',
      resetPassword: 'Reimposta password',
      sendResetLink: 'Invia link di ripristino',
      resetLinkSent: 'Link per la reimpostazione della password inviato! Controlla la tua email.',
      newPassword: 'Nuova password',
      updatePassword: 'Aggiorna password',
      passwordUpdated: 'Password aggiornata con successo!',
      selectShopToAuth: 'Seleziona un negozio per continuare',
      authFor: 'Autenticazione per {shop}',
      emailConfirmed: 'Email confermata con successo! Ora puoi accedere.',
    },
    booking: {
      bookNow: 'Prenota ora',
      myBookings: 'Le mie prenotazioni',
      chooseServices: 'Scegli i servizi',
      pickTime: 'Scegli un orario',
      yourDetails: 'I tuoi dettagli',
      confirmBooking: 'Conferma prenotazione',
      stepOf: 'Passaggio {step} di {total}',
      selectShop: 'Scegli un salone',
      selectBarber: 'Seleziona un barbiere',
      chooseLocation: 'Scegli la tua posizione preferita per prenotare un appuntamento.',
      chooseBarber: 'Scegli il tuo barbiere preferito per il tuo appuntamento.',
      changeShop: 'Cambia negozio',
      changeBarber: 'Cambia barbiere',
      totalSelection: 'Selezione totale',
      selectDate: 'Seleziona data',
      availableSlots: 'Slot disponibili',
      noSlots: 'Nessuno slot disponibile per questo giorno.',
      noBarbers: 'Nessun barbiere trovato per questo negozio.',
      finalDetails: 'Dettagli finali',
      yourInformation: 'Le tue informazioni',
      provideDetails: 'Fornisci i tuoi dati di contatto per la prenotazione.',
      fullName: 'Nome completo',
      emailAddress: 'Indirizzo email',
      phoneNumber: 'Numero di telefono',
      firstName: 'Nome',
      lastName: 'Cognome',
      companyName: 'Nome dell\'azienda (Nome del salone)',
      address: 'Indirizzo',
      reviewBooking: 'Rivedi prenotazione',
      changeTime: 'Cambia orario',
      confirmYourBooking: 'Conferma la tua prenotazione',
      reviewBeforeSubmission: 'Rivedi tutti i dettagli prima dell\'invio finale.',
      selectedServices: 'Servizi selezionati',
      shopAndTime: 'Negozio e ora',
      customerInformation: 'Informazioni cliente',
      confirmAndBook: 'Conferma e prenota ora',
      editDetails: 'Modifica dettagli',
      cancellationPolicy: 'Politica di cancellazione',
      cancellationTerms: 'Puoi annullare o riprogrammare il tuo appuntamento fino a 24 ore prima dell\'orario previsto. Le cancellazioni effettuate entro 24 ore comporteranno una penale del 50%.',
      acceptPolicy: 'Accetto la politica di cancellazione',
      youAreSharp: 'Sei al top!',
      confirmationSent: 'Abbiamo inviato un\'email di conferma a {email}.',
      seeYouAt: 'Ci vediamo da {shop} il {date} alle {time}!',
      bookAnother: 'Prenota un altro',
      yourBookings: 'Le tue prenotazioni',
      manageAppointments: 'Visualizza e gestisci i tuoi prossimi appuntamenti.',
      noBookings: 'Nessuna prenotazione trovata.',
      cancelBooking: 'Annulla prenotazione',
      cancelConfirm: 'Sei sicuro di voler annullare questa prenotazione?',
      manageBookingsPrompt: 'Vuoi gestire facilmente le tue prenotazioni?',
      loginOrSignUp: 'Accedi o Registrati',
      continueAsGuest: 'Continua come ospite',
      agreeToTerms: 'Accetto i {terms} e l\'{privacy}',
      mustAgree: 'Devi accettare i termini e le condizioni per continuare',
      bookSlot: 'Prenota slot',
    },
    pricing: {
      title: 'Eleva il tuo business di barbiere',
      subtitle: 'Unisciti alla rete d\'élite di barbieri che utilizzano il nostro sistema di gestione premium. Semplifica le tue prenotazioni, gestisci il tuo staff e fai crescere il tuo marchio.',
      proPlan: 'Pro Barber Shop',
      monthlySub: 'Abbonamento Mensile',
      getStarted: 'Inizia ora',
      cancelAnytime: 'Annulla in qualsiasi momento • Pagamento sicuro tramite Stripe',
      features: {
        unlimitedBarbers: 'Barbieri illimitati',
        unlimitedBookings: 'Prenotazioni illimitate',
        customServices: 'Servizi e categorie personalizzati',
        realtimeDash: 'Dashboard in tempo reale',
        emailNotifications: 'Notifiche email',
        multiLanguage: 'Supporto multilingue',
        prioritySupport: 'Supporto prioritario',
        advancedAnalytics: 'Analisi avanzate',
        upTo3Barbers: 'Fino a 3 barbieri',
        standardAnalytics: 'Analisi standard',
      },
      saveTwoMonths: 'Risparmia 2 mesi',
      fourteenDayTrial: 'Prova gratuita di 14 giorni',
      noChargeToday: 'Nessun addebito oggi • La prova inizia ora',
      billedMonthly: 'Fatturato mensilmente',
      billedAnnually: 'Fatturato annualmente',
      monthly: 'Mensile',
      annually: 'Annuale',
      basicDesc: 'Perfetto per i piccoli negozi che iniziano',
      proDesc: 'La solutione completa per i negozi in crescita',
      mostPopular: 'Il più popolare',
      iAgreeTo: 'ACCETTO I',
      and: 'E',
      subRequired: 'Abbonamento Richiesto',
      subRequiredDesc: 'Per accedere alla dashboard di amministrazione e gestire il tuo negozio, è necessario un abbonamento attivo.',
      viewPricing: 'Visualizza Prezzi',
      totalPerYear: 'Totale: {price} / anno',
      perMonth: 'al mese',
      perMonthBilledAnnually: 'al mese, fatturato annualmente',
      startTrial: 'Inizia la prova di 14 giorni',
      authRequired: 'Autenticazione richiesta',
      authDesc: 'Accedi o crea un account per iniziare il tuo abbonamento.',
    },
    legal: {
      terms: 'Termini e Condizioni',
      privacy: 'Informativa sulla Privacy',
      cookies: 'Informativa sui Cookie',
      cookieConsent: 'Utilizziamo i cookie per migliorare la tua esperienza. Continuando a visitare questo sito accetti il nostro utilizzo dei cookie.',
      accept: 'Accetta',
      decline: 'Rifiuta',
    },
    admin: {
      reservations: 'Prenotazioni',
      noReservations: 'Nessuna prenotazione trovata',
      customers: 'Clienti',
      slots: 'Slot',
      services: 'Servizi',
      team: 'Team',
      shops: 'Negozi',
      settings: 'Impostazioni',
      roles: 'Ruoli',
      userRoles: 'Ruoli Utente',
      platformUsers: 'Utenti della Piattaforma',
      systemOverview: 'Panoramica del Sistema',
      activeSubscriptions: 'Abbonamenti Attivi',
      totalPlatformUsers: 'Totale Utenti Piattaforma',
      platformHealth: 'Salute della Piattaforma',
      platformHealthDesc: 'La tua piattaforma funziona attualmente a livelli operativi. Tutti i sistemi sono operativi.',
      systemStable: 'Sistema Stabile',
      billingTransactions: 'Transazioni di Fatturazione',
      recentBilling: 'Fatturazione Recente',
      viewAllTransactions: 'Visualizza Tutte le Transazioni',
      shopLocationUpdated: 'Posizione del negozio aggiornata',
      failedUpdateLocation: 'Errore nell\'aggiornamento della posizione del negozio',
      enterValidCoords: 'Inserisci coordinate valide',
      deleteShopConfirm: 'Sei sicuro di voler eliminare questo negozio? L\'azione non può essere annullata.',
      shopControl: 'Controllo Negozio',
      superAdmin: 'Super Admin',
      barberAdmin: 'Admin Barbiere',
      barberStaff: 'Personale Barbiere',
      customerRole: 'Cliente',
      status: 'STATO',
      myDashboard: 'IL MIO DASHBOARD',
      chat: 'CHAT',
      adminDashboard: 'Dashboard Admin',
      superAdminDashboard: 'Dashboard Super Admin',
      menuItems: 'Voci del Menu',
      instantSetup: 'Configurazione istantanea',
      generateSlots: 'Genera slot',
      startTime: 'Ora di inizio',
      endTime: 'Ora di fine',
      intervalMins: 'Intervallo (min)',
      customInterval: 'Intervallo personalizzato',
      service: 'Servizio',
      breakStart: 'Inizio pausa',
      breakEnd: 'Fine pausa',
      clearAvailability: 'Cancella disponibilità',
      confirmClearAvailability: 'Sei sicuro di voler eliminare tutti gli slot disponibili per questo giorno?',
      slotsGenerated: 'Slot generati con successo',
      failedGenerateSlots: 'Impossibile generare gli slot',
      availabilityCleared: 'Disponibilità cancellata',
      failedClearAvailability: 'Impossibile cancellare la disponibilità',
      description: 'Descrizione del servizio',
      addService: 'Aggiungi Servizio',
      searchServices: 'Cerca servizi...',
      searchTeam: 'Cerca team...',
      searchReservations: 'Cerca prenotazioni...',
      searchShops: 'Cerca negozi...',
      searchUsers: 'Cerca utenti...',
      searchTransactions: 'Cerca transazioni...',
      totalBookings: 'Prenotazioni totali',
      lastBooking: 'Ultima prenotazione',
      recentReservations: 'Prenotazioni recenti',
      customer: 'Cliente',
      dateAndTime: 'Data e ora',
      addSlot: 'Aggiungi slot',
      manageStaff: 'Gestisci il personale e i ruoli di amministratore',
      allRoles: 'Tutti i ruoli',
      allCategories: 'Tutte le categorie',
      allBarbers: 'Tutti i barbieri',
      shop: 'Negozio',
      allShops: 'Tutti i negozi (Panoramica)',
      signOut: 'Esci',
      profileNotFound: 'Profilo non trovato',
      accessDenied: 'Accesso negato',
      selectCategory: 'Seleziona categoria',
      haircut: 'Taglio di capelli',
      shave: 'Rasatura',
      styling: 'Styling',
      treatment: 'Trattamento',
      joined: 'Iscritto il',
      currentRole: 'Ruolo attuale',
      barberShop: 'Barber Shop',
      actions: 'Azioni',
      makeSuperAdmin: 'Rendi Super Admin',
      makeAdmin: 'Rendi Admin del Negozio',
      makeStaff: 'Rendi Staff Barbiere',
      revoke: 'Revoca',
      noShopAssigned: 'Nessun negozio assegnato',
      noShop: 'Nessun negozio',
      updateRole: 'Aggiorna ruolo utente',
      confirmUpdateRole: 'Sei sicuro di voler cambiare il ruolo di questo utente? Ciò potrebbe concedere o limitare l\'accesso alle funzioni amministrative.',
      currentUser: 'Utente attuale',
      manageSystemRoles: 'Gestisci i permessi utente a livello di sistema',
      general: 'Generale',
      hours: 'Orari',
      shopStatus: 'Stato del negozio',
      activateDeactivateShop: 'Abilita o disabilita questo negozio',
      manageShopDetails: 'Gestisci i dettagli del tuo barber shop',
      shopName: 'Nome del negozio',
      address: 'Indirizzo',
      openingTime: 'Orario di apertura',
      closingTime: 'Orario di chiusura',
      appointmentReminders: 'Promemoria appuntamenti',
      enableReminders: 'Attiva promemoria',
      enableRemindersDesc: 'Invia promemoria automatici ai clienti',
      reminderType: 'Tipo di promemoria',
      emailOnly: 'Solo e-mail',
      smsOnly: 'Solo SMS',
      bothEmailSms: 'E-mail e SMS',
      reminderTypeDesc: 'Scegli come i clienti ricevono i promemoria degli appuntamenti',
      hoursBefore: 'Ore prima',
      sendingReminders: 'Invio promemoria...',
      sendRemindersManual: 'Invia promemoria ora (attivazione manuale)',
      longitude: 'Longitudine',
      latitude: 'Latitudine',
      updateShopDetails: 'Aggiorna i dettagli del negozio',
      onlyAdminsModify: 'Solo gli amministratori del negozio possono modificare queste impostazioni.',
      noShopSelected: 'Nessun negozio selezionato o assegnato.',
      user: 'Utente',
      category: 'Categoria',
      min: 'Minuti',
      photoUrl: 'Foto',
      price: 'Prezzo',
      bio: 'Biografia',
      linkUser: 'Collega a un account utente',
      noUser: 'Nessun utente collegato',
      addBarber: 'Aggiungi barbiere',
      editBarber: 'Modifica barbiere',
      editLocation: 'Modifica Posizione',
      barberAdded: 'Barbiere aggiunto con successo',
      barberUpdated: 'Barbiere aggiornato con successo',
      barberDeleted: 'Barbiere eliminato con successo',
      linkedBarber: 'Barbiere collegato',
      break: 'Orari di pausa',
      changePhoto: 'Cambia foto',
      uploadPhoto: 'Carica foto',
      photoUploadHint: 'Consigliato: Rapporto d\'aspetto quadrato, max 2 MB',
      failedAddBarber: 'Impossibile aggiungere il barbiere',
      failedUpdateBarber: 'Impossibile aggiornare il barbiere',
      failedDeleteBarber: 'Impossibile eliminare il barbiere',
      deleteBarber: 'Elimina barbiere',
      confirmDeleteBarber: 'Sei sicuro di voler eliminare questo barbiere?',
      deleteService: 'Elimina servizio',
      confirmDeleteService: 'Sei sicuro di voler eliminare questo servizio?',
      deleteSlot: 'Elimina slot',
      confirmDeleteSlot: 'Sei sicuro di voler eliminare questa fascia oraria?',
      cancelReservation: 'Annulla prenotazione',
      confirmCancelReservation: 'Sei sicuro di voler annullare questa prenotazione?',
      roleUpdated: 'Ruolo aggiornato a {role}',
      reservationStatusUpdated: 'Stato della prenotazione aggiornato a {status}',
      reservationUpdated: 'Prenotazione aggiornata con successo',
      failedUpdateReservation: 'Impossibile aggiornare la prenotazione',
      editReservation: 'Modifica prenotazione',
      customerDetails: 'Dettagli cliente',
      newReservation: 'Nuova prenotazione',
      newBookingMade: 'È stata effettuata una nuova prenotazione.',
      failedUpdateRole: 'Impossibile aggiornare il ruolo',
      categories: 'Categorie',
      addCategory: 'Aggiungi categoria',
      categoryExists: 'La categoria esiste già',
      categoryAdded: 'Categoria aggiunta con successo',
      categoryDeleted: 'Categoria eliminata con successo',
      categoryUpdated: 'Categoria aggiornata con successo',
      failedUpdateCategory: 'Impossibile aggiornare la categoria',
      categoryInUse: 'Impossibile eliminare una categoria in uso',
      addNewCategory: 'Aggiungi nuova categoria...',
      selectShopFirst: 'Seleziona prima un negozio',
      slotExists: 'Questa fascia oraria esiste già',
      slotAdded: 'Slot aggiunto con successo',
      failedAddSlot: 'Impossibile aggiungere lo slot',
      slotDeleted: 'Slot eliminato con successo',
      failedDeleteSlot: 'Impossibile eliminare lo slot',
      failedUpdateStatus: 'Impossibile aggiornare lo stato',
      selectBarberFirst: 'Seleziona prima un barbiere',
      invalidPriceDuration: 'Prezzo o durata non validi',
      serviceAdded: 'Servizio aggiunto con successo',
      serviceUpdated: 'Servizio aggiornato con successo',
      failedAddService: 'Impossibile aggiungere il servizio',
      failedUpdateService: 'Impossibile aggiornare il servizio',
      serviceDeleted: 'Servizio eliminato con successo',
      failedDeleteService: 'Impossibile eliminare il servizio',
      shopNameRequired: 'Il nome del negozio è obbligatorio',
      invalidClosingTime: 'L\'orario di chiusura deve essere successivo all\'orario di apertura',
      shopAdded: 'Negozio aggiunto con successo',
      failedAddShop: 'Impossibile aggiungere il negozio',
      confirmDeleteShop: 'Sei sicuro di voler eliminare questo negozio?',
      shopDeleted: 'Negozio eliminato con successo',
      failedDeleteShop: 'Impossibile eliminare il negozio',
      deleteShop: 'Elimina negozio',
      shopUpdated: 'Negozio aggiornato con successo',
      failedUpdateShop: 'Impossibile aggiornare il negozio',
      remindersNotEnabled: 'I promemoria non sono attivati per questo negozio',
      failedSendReminders: 'Impossibile inviare i promemoria',
      reminderSent: 'Promemoria inviato con successo',
      remindersSent: '{count} promemoria inviati con successo',
      reminder: 'Promemoria',
      sent: 'Inviato',
      notSent: 'Non inviato',
      sendReminder: 'Invia promemoria',
      profileNotFoundDesc: 'Impossibile trovare il tuo profilo utente.',
      accessDeniedDesc: 'Non hai il permesso di accedere a questa pagina.',
      control: 'Controllo',
      allStatus: 'Tutti gli stati',
      dateTime: 'Data e ora',
      addSlots: 'Aggiungi slot',
      timeSlot: 'Fascia oraria',
      availability: 'Disponibilità',
      manageStaffAvailability: 'Gestisci la disponibilità del personale e del negozio',
      slotsForDay: 'Slot per il giorno selezionato',
      for: 'per',
      showingSlotsFor: 'Visualizzazione slot per',
      noSlotsAdded: 'Nessuno slot aggiunto per questo giorno',
      addFirst: 'Aggiungi slot',
      availableSlots: 'Slot disponibili',
      teamManagement: 'Gestione del team',
      manageStaffRoles: 'Gestisci il personale e i ruoli di amministratore',
      addStaff: 'Aggiungi account personale',
      staffAccountCreated: 'Account personale creato con successo',
      failedCreateStaff: 'Impossibile creare l\'account personale',
      staffCreationNotice: 'Questo creerà un nuovo account di accesso per il tuo membro del personale. Verrà assegnato automaticamente al tuo negozio.',
      createAccount: 'Crea account',
      instructions: 'Istruzioni',
      teamInstructions: 'Gestisci i membri del tuo team e assegnali a negozi specifici. Gli amministratori possono gestire servizi e slot per il negozio assegnato.',
      menu: 'Menu',
      items: 'Voci',
      subscription: 'Abbonamento',
      currentPlan: 'Piano Attuale',
      renewsOn: 'Rinnova il',
      manageSubscription: 'Gestisci Abbonamento',
      trialEndsOn: 'La prova termina il',
      serviceName: 'Nome del servizio',
      opening: 'Apertura',
      closing: 'Chiusura',
      barber: 'Barbiere',
      bulkCreateAvailability: 'Crea disponibilità in blocco',
      workingDays: 'Giorni lavorativi',
      startDate: 'Data di inizio',
      endDate: 'Data di fine',
      quickTips: 'Suggerimenti rapidi',
      tip1: 'Seleziona prima un barbiere per gestire la sua disponibilità specifica.',
      tip2: 'Usa il generatore di massa per impostare rapidamente un intero mese di slot.',
      tip3: 'Clicca su uno slot prenotato per visualizzare i dettagli dell\'appuntamento.',
      noSlotsShort: 'No Slot',
      addItem: 'Aggiungi articolo',
      addShop: 'Aggiungi negozio',
      automatedCampaigns: 'Campagne automatizzate',
      campaignsDesc: 'Imposta messaggi di marketing automatici',
      promoCodes: 'Codici promozionali',
      addPromo: 'Aggiungi promo',
      code: 'Codice',
      discount: 'Sconto',
      expires: 'Scade',
      lowStock: 'Scorta bassa',
      inStock: 'In magazzino',
      item: 'Articolo',
      stock: 'Scorta',
      minStock: 'Scorta min',
      amount: 'Importo',
      commissions: 'Commissioni',
    },
    home: {
      heroTitle: 'Esperienza di Barbiere Vibrante',
      heroSubtitle: 'Prenota il tuo prossimo taglio con i migliori in città. Servizio professionale, stile moderno.',
      bookNow: 'Prenota Ora',
      ourServices: 'I Nostri Servizi',
      contactUs: 'Contattaci',
    }
  },
  ta: {
    common: {
      save: 'சேமி',
      cancel: 'ரத்துசெய்',
      delete: 'நீக்கு',
      search: 'தேடு',
      loading: 'ஏற்றுகிறது...',
      all: 'அனைத்தும்',
      confirm: 'உறுதிப்படுத்து',
      pending: 'நிலுவையில் உள்ளது',
      cancelled: 'ரத்து செய்யப்பட்டது',
      confirmed: 'உறுதிப்படுத்தப்பட்டது',
      back: 'பின்செல்',
      continue: 'தொடரவும்',
      processing: 'செயலாக்குகிறது...',
      saving: 'சேமிக்கிறது...',
      total: 'மொத்தம்',
      status: 'நிலை',
      actions: 'செயல்கள்',
      view: 'பார்',
      edit: 'திருத்து',
      name: 'பெயர்',
      email: 'மின்னஞ்சல்',
      phone: 'தொலைபேசி',
      address: 'முகவரி',
      date: 'தேதி',
      time: 'நேரம்',
      price: 'விலை',
      duration: 'காலம்',
      category: 'வகை',
      description: 'விளக்கம்',
      available: 'கிடைக்கிறது',
      booked: 'முன்பதிவு செய்யப்பட்டது',
      sharp: 'Sharp',
      cuts: 'Cuts',
      est: 'நிறுவப்பட்டது 2024 • பிரீமியம் பார்பரிங்',
      currency: 'CHF',
      none: 'எதுவுமில்லை',
      selectLanguage: 'மொழியைத் தேர்ந்தெடுக்கவும்',
      na: 'கிடைக்கவில்லை',
      noAddress: 'முகவரி வழங்கப்படவில்லை',
      today: 'இன்று',
      min: 'நிமிடங்கள்',
      any: 'ஏதாவது',
      away: 'தூரத்திலுள்ள',
      customer: 'வாடிக்கையாளர்',
      copyright: '© {year} Sharp Cuts. அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.',
      terms: 'விதிமுறைகள் மற்றும் நிபந்தனைகள்',
      privacy: 'தனியுரிமைக் கொள்கை',
      cookies: 'குக்கீ கொள்கை',
      swissLaw: 'சுவிஸ் சட்டத்திற்கு இணங்க',
      validation: {
        required: 'இந்த புலம் தேவை',
        email: 'தவறான மின்னஞ்சல் முகவரி',
        phone: 'தவறான சுவிஸ் தொலைபேசி எண்',
        passwordMin: 'கடவுச்சொல் குறைந்தது 8 எழுத்துக்கள் இருக்க வேண்டும்',
        passwordMatch: 'கடவுச்சொற்கள் பொருந்தவில்லை',
        nameMin: 'பெயர் குறைந்தது 2 எழுத்துக்கள் இருக்க வேண்டும்'
      },
      days: {
        sun: 'ஞாயிறு',
        mon: 'திங்கள்',
        tue: 'செவ்வாய்',
        wed: 'புதன்',
        thu: 'வியாழன்',
        fri: 'வெள்ளி',
        sat: 'சனி'
      }
    },
    setup: {
      required: 'அமைப்பு தேவை',
      dbError: 'தரவுத்தள பிழை',
      configureSupabase: 'பார்பர் ஷாப் அமைப்பைத் தொடங்க உங்கள் Supabase சான்றுகளை .env இல் உள்ளமைக்கவும்.',
      requiredVariables: 'தேவையான மாறிகள்:',
      nextSteps: 'அடுத்த படிகள்:',
      openDashboard: 'உங்கள் Supabase டாஷ்போர்டைத் திறக்கவும்',
      goSqlEditor: 'SQL எடிட்டருக்குச் செல்லவும்',
      runSchema: 'supabase_schema.sql இன் உள்ளடக்கங்களை ஒட்டி இயக்கவும்',
      refreshPage: 'இந்த பக்கத்தைப் புதுப்பிக்கவும்',
      dbSchemaNotFound: 'தரவுத்தள ஸ்கீமா கிடைக்கவில்லை. உங்கள் Supabase டாஷ்போர்டில் SQL ஸ்கீமாவை இயக்கவும்.',
    },
    nav: {
      home: 'முகப்பு',
      dashboard: 'டாஷ்போர்டு',
      logout: 'வெளியேறு',
      login: 'உள்நுழை',
      register: 'பதிவு செய்',
      booking: 'முன்பதிவு',
      book: 'முன்பதிவு',
      history: 'கணக்கு',
      admin: 'நிர்வாகம்',
    },
    auth: {
      signIn: 'உள்நுழைக',
      signUp: 'பதிவு செய்க',
      createAccount: 'கணக்கை உருவாக்கு',
      alreadyHaveAccount: 'ஏற்கனவே கணக்கு உள்ளதா? உள்நுழைக',
      dontHaveAccount: 'கணக்கு இல்லையா? பதிவு செய்க',
      password: 'கடவுச்சொல்',
      confirmPassword: 'கடவுச்சொல்லை உறுதிப்படுத்து',
      passwordsDoNotMatch: 'கடவுச்சொற்கள் பொருந்தவில்லை',
      verifyEmail: 'உங்கள் மின்னஞ்சலை சரிபார்க்கவும்',
      checkEmail: 'உங்கள் மின்னஞ்சலுக்கு ஒரு பாதுகாப்பு இணைப்பை அனுப்பியுள்ளோம். உங்கள் கணக்கைச் செயல்படுத்த இணைப்பைக் கிளிக் செய்யவும்.',
      didntReceive: 'கிடைக்கவில்லையா?',
      resendEmail: 'உறுதிப்படுத்தல் மின்னஞ்சலை மீண்டும் அனுப்பு',
      resendIn: '{seconds} வினாடிகளில் மீண்டும் அனுப்பவும்',
      backToSignIn: 'மீண்டும் உள்நுழைவுக்குச் செல்லவும்',
      checkSpam: 'உங்கள் ஸ்பேம் அல்லது ஜங்க் கோப்புறையைச் சரிபார்க்கவும்',
      waitMinutes: 'டெலிவரிக்கு சில நிமிடங்கள் காத்திருக்கவும்',
      ensureEmail: 'மின்னஞ்சல் முகவரி சரியானது என்பதை உறுதிப்படுத்தவும்',
      adminAccess: 'நிர்வாக அணுகல்',
      joinCrew: 'குழுவில் சேருங்கள்',
      signInToManage: 'உங்கள் கடையை நிர்வகிக்க உள்நுழையவும்',
      customerLogin: 'வாடிக்கையாளர் உள்நுழைவு',
      signUpToBook: 'உங்கள் முன்பதிவுகளை நிர்வகிக்க ஒரு கணக்கை உருவாக்கவும்',
      signInToBook: 'உங்கள் முன்பதிவுகளை நிர்வகிக்க உள்நுழையவும்',
      strengthVeryWeak: 'மிகவும் பலவீனமானது',
      strengthWeak: 'பலவீனமானது',
      strengthMedium: 'நடுத்தரம்',
      strengthStrong: 'வலிமையானது',
      strengthVeryStrong: 'மிகவும் வலிமையானது',
      forgotPassword: 'கடவுச்சொல்லை மறந்துவிட்டீர்களா?',
      resetPassword: 'கடவுச்சொல்லை மீட்டமைக்கவும்',
      sendResetLink: 'மீட்டமைப்பு இணைப்பை அனுப்பவும்',
      resetLinkSent: 'கடவுச்சொல் மீட்டமைப்பு இணைப்பு அனுப்பப்பட்டது! உங்கள் மின்னஞ்சலைச் சரிபார்க்கவும்.',
      newPassword: 'புதிய கடவுச்சொல்',
      updatePassword: 'கடவுச்சொல்லைப் புதுப்பிக்கவும்',
      passwordUpdated: 'கடவுச்சொல் வெற்றிகரமாகப் புதுப்பிக்கப்பட்டது!',
      selectShopToAuth: 'தொடர ஒரு கடையைத் தேர்ந்தெடுக்கவும்',
      authFor: '{shop} க்கான அங்கீகாரம்',
      emailConfirmed: 'மின்னஞ்சல் வெற்றிகரமாக உறுதிப்படுத்தப்பட்டது! நீங்கள் இப்போது உள்நுழையலாம்.',
    },
    booking: {
      bookNow: 'இப்போது முன்பதிவு செய்யுங்கள்',
      myBookings: 'எனது முன்பதிவுகள்',
      chooseServices: 'சேவைகளைத் தேர்ந்தெடுக்கவும்',
      pickTime: 'நேரத்தைத் தேர்ந்தெடுக்கவும்',
      yourDetails: 'உங்கள் விவரங்கள்',
      confirmBooking: 'முன்பதிவை உறுதிப்படுத்தவும்',
      stepOf: '{total}-இல் {step}-வது படி',
      selectShop: 'கடையைத் தேர்ந்தெடுக்கவும்',
      selectBarber: 'ஒரு பார்பரைத் தேர்ந்தெடுக்கவும்',
      chooseLocation: 'சந்திப்பை முன்பதிவு செய்ய உங்களுக்கு விருப்பமான இடத்தைத் தேர்ந்தெடுக்கவும்.',
      chooseBarber: 'உங்கள் சந்திப்பிற்கு உங்களுக்கு விருப்பமான பார்பரைத் தேர்ந்தெடுக்கவும்.',
      changeShop: 'கடையை மாற்றவும்',
      changeBarber: 'பார்பரை மாற்றவும்',
      totalSelection: 'மொத்தத் தேர்வு',
      selectDate: 'தேதியைத் தேர்ந்தெடுக்கவும்',
      availableSlots: 'கிடைக்கக்கூடிய நேரங்கள்',
      noSlots: 'இந்த நாளில் நேரங்கள் எதுவும் இல்லை.',
      noBarbers: 'இந்த கடைக்கு பார்பர்கள் யாரும் இல்லை.',
      finalDetails: 'இறுதி விவரங்கள்',
      yourInformation: 'உங்கள் தகவல்',
      provideDetails: 'முன்பதிவுக்கான உங்கள் தொடர்பு விவரங்களை வழங்கவும்.',
      fullName: 'முழு பெயர்',
      emailAddress: 'மின்னஞ்சல் முகவரி',
      phoneNumber: 'தொலைபேசி எண்',
      firstName: 'முதல் பெயர்',
      lastName: 'கடைசி பெயர்',
      companyName: 'நிறுவனத்தின் பெயர் (பார்பர் ஷாப் பெயர்)',
      address: 'முகவரி',
      reviewBooking: 'முன்பதிவைச் சரிபார்க்கவும்',
      changeTime: 'நேரத்தை மாற்றவும்',
      confirmYourBooking: 'உங்கள் முன்பதிவை உறுதிப்படுத்தவும்',
      reviewBeforeSubmission: 'இறுதி சமர்ப்பிப்பிற்கு முன் அனைத்து விவரங்களையும் சரிபார்க்கவும்.',
      selectedServices: 'தேர்ந்தெடுக்கப்பட்ட சேவைகள்',
      shopAndTime: 'கடை மற்றும் நேரம்',
      customerInformation: 'வாடிக்கையாளர் தகவல்',
      confirmAndBook: 'உறுதிப்படுத்தி இப்போது முன்பதிவு செய்யுங்கள்',
      editDetails: 'விவரங்களைத் திருத்தவும்',
      cancellationPolicy: 'ரத்துசெய்தல் கொள்கை',
      cancellationTerms: 'உங்கள் சந்திப்பை திட்டமிடப்பட்ட நேரத்திற்கு 24 மணிநேரத்திற்கு முன்பு வரை ரத்து செய்யலாம் அல்லது மாற்றியமைக்கலாம். 24 மணிநேரத்திற்குள் ரத்து செய்யப்பட்டால் 50% கட்டணம் விதிக்கப்படும்.',
      acceptPolicy: 'ரத்துசெய்தல் கொள்கையை நான் ஏற்கிறேன்',
      youAreSharp: 'நீங்கள் கூர்மையானவர்!',
      confirmationSent: '{email}-க்கு உறுதிப்படுத்தல் மின்னஞ்சலை அனுப்பியுள்ளோம்.',
      seeYouAt: '{date} அன்று {time} மணிக்கு {shop}-இல் சந்திப்போம்!',
      bookAnother: 'மற்றொன்றை முன்பதிவு செய்யவும்',
      yourBookings: 'உங்கள் முன்பதிவுகள்',
      manageAppointments: 'உங்கள் வரவிருக்கும் சந்திப்புகளைப் பார்த்து நிர்வகிக்கவும்.',
      noBookings: 'முன்பதிவுகள் எதுவும் இல்லை.',
      cancelBooking: 'முன்பதிவை ரத்துசெய்',
      cancelConfirm: 'இந்த முன்பதிவை ரத்து செய்ய விரும்புகிறீர்களா?',
      manageBookingsPrompt: 'உங்கள் முன்பதிவுகளை எளிதாக நிர்வகிக்க விரும்புகிறீர்களா?',
      loginOrSignUp: 'உள்நுழையவும் அல்லது பதிவு செய்யவும்',
      continueAsGuest: 'விருந்தினராக தொடரவும்',
      agreeToTerms: '{terms} மற்றும் {privacy}யை நான் ஒப்புக்கொள்கிறேன்',
      mustAgree: 'தொடர நீங்கள் விதிமுறைகள் மற்றும் நிபந்தனைகளை ஒப்புக்கொள்ள வேண்டும்',
      bookSlot: 'நேரத்தை முன்பதிவு செய்',
    },
    pricing: {
      title: 'உங்கள் பார்பர் வணிகத்தை மேம்படுத்தவும்',
      subtitle: 'எங்கள் பிரீமியம் மேலாண்மை முறையைப் பயன்படுத்தி பார்பர் கடைகளின் உயரடுக்கு நெட்வொர்க்கில் சேரவும். உங்கள் முன்பதிவுகளை நெறிப்படுத்துங்கள், உங்கள் ஊழியர்களை நிர்வகிக்கவும் மற்றும் உங்கள் பிராண்டை வளர்க்கவும்.',
      proPlan: 'புரோ பார்பர் ஷாப்',
      monthlySub: 'மாதாந்திர சந்தா',
      getStarted: 'இப்போதே தொடங்கவும்',
      cancelAnytime: 'எப்போது வேண்டுமானாலும் ரத்து செய்யலாம் • ஸ்ட்ரைப் மூலம் பாதுகாப்பான கட்டணம்',
      features: {
        unlimitedBarbers: 'வரம்பற்ற பார்பர்கள்',
        unlimitedBookings: 'வரம்பற்ற முன்பதிவுகள்',
        customServices: 'தனிப்பயன் சேவைகள் மற்றும் வகைகள்',
        realtimeDash: 'நிகழ்நேர டாஷ்போர்டு',
        emailNotifications: 'மின்னஞ்சல் அறிவிப்புகள்',
        multiLanguage: 'பல்மொழி ஆதரவு',
        prioritySupport: 'முன்னுரிமை ஆதரவு',
        advancedAnalytics: 'மேம்பட்ட பகுப்பாய்வு',
        upTo3Barbers: '3 பார்பர்கள் வரை',
        standardAnalytics: 'நிலையான பகுப்பாய்வு',
      },
      saveTwoMonths: '2 மாதங்களைச் சேமிக்கவும்',
      fourteenDayTrial: '14 நாள் இலவச சோதனை',
      noChargeToday: 'இன்று கட்டணம் இல்லை • சோதனை இப்போது தொடங்குகிறது',
      billedMonthly: 'மாதாந்திர கட்டணம்',
      billedAnnually: 'ஆண்டு கட்டணம்',
      monthly: 'மாதாந்திர',
      annually: 'ஆண்டு',
      basicDesc: 'ஆரம்பத்தில் இருக்கும் சிறிய கடைகளுக்கு ஏற்றது',
      proDesc: 'வளரும் கடைகளுக்கான முழுமையான தீர்வு',
      mostPopular: 'மிகவும் பிரபலமானது',
      iAgreeTo: 'நான் ஒப்புக்கொள்கிறேன்',
      and: 'மற்றும்',
      totalPerYear: 'மொத்தம்: {price} / ஆண்டு',
      perMonth: 'மாதத்திற்கு',
      perMonthBilledAnnually: 'மாதத்திற்கு, ஆண்டுதோறும் வசூலிக்கப்படும்',
      startTrial: '14 நாள் இலவச சோதனையைத் தொடங்கவும்',
      authRequired: 'அங்கீகாரம் தேவை',
      authDesc: 'உங்கள் சந்தாவைத் தொடங்க உள்நுழையவும் அல்லது கணக்கை உருவாக்கவும்.',
    },
    legal: {
      terms: 'விதிமுறைகள் மற்றும் நிபந்தனைகள்',
      privacy: 'தனியுரிமைக் கொள்கை',
      cookies: 'குக்கீ கொள்கை',
      cookieConsent: 'உங்கள் அனுபவத்தை மேம்படுத்த நாங்கள் குக்கீகளைப் பயன்படுத்துகிறோம். இந்தத் தளத்தைத் தொடர்ந்து பார்வையிடுவதன் மூலம் எங்களின் குக்கீகளின் பயன்பாட்டை ஏற்கிறீர்கள்.',
      accept: 'ஏற்றுக்கொள்',
      decline: 'நிராகரி',
    },
    admin: {
      reservations: 'முன்பதிவுகள்',
      noReservations: 'முன்பதிவுகள் எதுவும் இல்லை',
      customers: 'வாடிக்கையாளர்கள்',
      slots: 'நேரங்கள்',
      services: 'சேவைகள்',
      team: 'குழு',
      shops: 'கடைகள்',
      settings: 'அமைப்புகள்',
      roles: 'பாத்திரங்கள்',
      userRoles: 'பயனர் பாத்திரங்கள்',
      shopControl: 'கடை கட்டுப்பாடு',
      deleteShopConfirm: 'நிச்சயமாக இந்த கடையை நீக்க விரும்புகிறீர்களா? இந்த செயலை மாற்ற முடியாது.',
      superAdmin: 'சூப்பர் அட்மின்',
      barberAdmin: 'கடை நிர்வாகி',
      barberStaff: 'பார்பர் ஊழியர்',
      customerRole: 'வாடிக்கையாளர்',
      status: 'நிலை',
      myDashboard: 'என் டாஷ்போர்டு',
      chat: 'அரட்டை',
      adminDashboard: 'அட்மின் டாஷ்போர்டு',
      superAdminDashboard: 'சூப்பர் அட்மின் டாஷ்போர்டு',
      menuItems: 'மெனு உருப்படிகள்',
      instantSetup: 'உடனடி அமைப்பு',
      generateSlots: 'நேரங்களை உருவாக்கு',
      startTime: 'தொடக்க நேரம்',
      endTime: 'முடிவு நேரம்',
      intervalMins: 'இடைவெளி (நிமிடங்கள்)',
      customInterval: 'தனிப்பயன் இடைவெளி',
      service: 'சேவை',
      breakStart: 'இடைவேளை தொடக்கம்',
      breakEnd: 'இடைவேளை முடிவு',
      clearAvailability: 'கிடைக்கும் தன்மையை அழி',
      confirmClearAvailability: 'இந்த நாளுக்கான அனைத்து கிடைக்கும் நேரங்களையும் நீக்க விரும்புகிறீர்களா?',
      slotsGenerated: 'நேரங்கள் வெற்றிகரமாக உருவாக்கப்பட்டன',
      failedGenerateSlots: 'நேரங்களை உருவாக்குவதில் தோல்வி',
      availabilityCleared: 'கிடைக்கும் தன்மை அழிக்கப்பட்டது',
      failedClearAvailability: 'கிடைக்கும் தன்மையை அழிப்பதில் தோல்வி',
      description: 'சேவை விளக்கம்',
      addService: 'சேவையைச் சேர்',
      searchServices: 'சேவைகளைத் தேடு...',
      searchTeam: 'குழுவைத் தேடு...',
      searchReservations: 'முன்பதிவுகளைத் தேடு...',
      totalBookings: 'மொத்த முன்பதிவுகள்',
      lastBooking: 'கடைசி முன்பதிவு',
      recentReservations: 'சமீபத்திய முன்பதிவுகள்',
      customer: 'வாடிக்கையாளர்',
      dateAndTime: 'தேதி மற்றும் நேரம்',
      addSlot: 'நேரத்தைச் சேர்',
      manageStaff: 'பணியாளர்கள் மற்றும் நிர்வாகப் பாத்திரங்களை நிர்வகிக்கவும்',
      addStaff: 'பணியாளர் கணக்கைச் சேர்க்கவும்',
      staffAccountCreated: 'பணியாளர் கணக்கு வெற்றிகரமாக உருவாக்கப்பட்டது',
      failedCreateStaff: 'பணியாளர் கணக்கை உருவாக்க முடியவில்லை',
      staffCreationNotice: 'இது உங்கள் பணியாளருக்கு ஒரு புதிய உள்நுழைவு கணக்கை உருவாக்கும். அவர்கள் தானாகவே உங்கள் கடைக்கு ஒதுக்கப்படுவார்கள்.',
      createAccount: 'கணக்கை உருவாக்கு',
      allRoles: 'அனைத்துப் பாத்திரங்கள்',
      allCategories: 'அனைத்து வகைகளும்',
      allBarbers: 'அனைத்து பார்பர்களும்',
      shop: 'கடை',
      allShops: 'அனைத்து கடைகள் (மேலோட்டம்)',
      signOut: 'வெளியேறு',
      profileNotFound: 'சுயவிவரம் கிடைக்கவில்லை',
      accessDenied: 'அணுகல் மறுக்கப்பட்டது',
      selectCategory: 'வகையைத் தேர்ந்தெடுக்கவும்',
      haircut: 'முடி வெட்டுதல்',
      shave: 'சவரம்',
      styling: 'ஸ்டைலிங்',
      treatment: 'சிகிச்சை',
      joined: 'சேர்ந்தார்',
      currentRole: 'தற்போதைய பங்கு',
      barberShop: 'பார்பர் ஷாப்',
      actions: 'செயல்கள்',
      makeSuperAdmin: 'சூப்பர் அட்மின் ஆக்கு',
      makeAdmin: 'கடை நிர்வாகியாக்கு',
      makeStaff: 'பார்பர் ஊழியராக்கு',
      revoke: 'திரும்பப் பெறு',
      noShopAssigned: 'கடை ஒதுக்கப்படவில்லை',
      noShop: 'கடை இல்லை',
      updateRole: 'பயனர் பாத்திரத்தைப் புதுப்பிக்கவும்',
      confirmUpdateRole: 'இந்தப் பயனரின் பாத்திரத்தை மாற்ற வேண்டுமா? இது நிர்வாக அம்சங்களுக்கான அணுகலை வழங்கலாம் அல்லது கட்டுப்படுத்தலாம்.',
      currentUser: 'தற்போதைய பயனர்',
      manageSystemRoles: 'கணினி அளவிலான பயனர் அனுமதிகளை நிர்வகிக்கவும்',
      general: 'பொதுவானவை',
      hours: 'நேரங்கள்',
      shopStatus: 'கடை நிலை',
      activateDeactivateShop: 'இந்த கடையை இயக்கவும் அல்லது முடக்கவும்',
      manageShopDetails: 'உங்கள் பார்பர் ஷாப் விவரங்களை நிர்வகிக்கவும்',
      shopName: 'கடையின் பெயர்',
      address: 'முகவரி',
      openingTime: 'திறக்கும் நேரம்',
      closingTime: 'மூடும் நேரம்',
      appointmentReminders: 'சந்திப்பு நினைவூட்டல்கள்',
      enableReminders: 'நினைவூட்டல்களை இயக்கு',
      enableRemindersDesc: 'வாடிக்கையாளர்களுக்கு தானியங்கி நினைவூட்டல்களை அனுப்பவும்',
      reminderType: 'நினைவூட்டல் வகை',
      emailOnly: 'மின்னஞ்சல் மட்டும்',
      smsOnly: 'SMS மட்டும்',
      bothEmailSms: 'மின்னஞ்சல் மற்றும் SMS',
      reminderTypeDesc: 'வாடிக்கையாளர்கள் தங்கள் சந்திப்பு நினைவூட்டல்களை எவ்வாறு பெறுகிறார்கள் என்பதைத் தேர்ந்தெடுக்கவும்',
      hoursBefore: 'மணிநேரத்திற்கு முன்',
      sendingReminders: 'நினைவூட்டல்கள் அனுப்பப்படுகின்றன...',
      sendRemindersManual: 'இப்போது நினைவூட்டல்களை அனுப்பவும் (கையேடு தூண்டுதல்)',
      longitude: 'தீர்க்கரேகை',
      latitude: 'அட்சரேகை',
      updateShopDetails: 'கடை விவரங்களைப் புதுப்பிக்கவும்',
      onlyAdminsModify: 'கடை நிர்வாகிகள் மட்டுமே இந்த அமைப்புகளை மாற்ற முடியும்.',
      noShopSelected: 'கடை எதுவும் தேர்ந்தெடுக்கப்படவில்லை அல்லது ஒதுக்கப்படவில்லை.',
      user: 'பயனர்',
      category: 'வகை',
      min: 'நிமிடங்கள்',
      photoUrl: 'புகைப்படம்',
      price: 'விலை',
      bio: 'சுயவிவரம்',
      linkUser: 'பயனர் கணக்குடன் இணைக்கவும்',
      noUser: 'பயனர் இணைக்கப்படவில்லை',
      addBarber: 'பார்பரைச் சேர்க்கவும்',
      editBarber: 'பார்பரைத் திருத்து',
      barberAdded: 'பார்பர் வெற்றிகரமாக சேர்க்கப்பட்டார்',
      barberUpdated: 'பார்பர் வெற்றிகரமாக புதுப்பிக்கப்பட்டார்',
      barberDeleted: 'பார்பர் வெற்றிகரமாக நீக்கப்பட்டார்',
      linkedBarber: 'இணைக்கப்பட்ட பார்பர்',
      break: 'இடைவேளை நேரங்கள்',
      changePhoto: 'புகைப்படத்தை மாற்றவும்',
      uploadPhoto: 'புகைப்படத்தைப் பதிவேற்றவும்',
      photoUploadHint: 'பரிந்துரைக்கபட்டது: சதுர விகிதம், அதிகபட்சம் 2MB',
      failedAddBarber: 'பார்பரைச் சேர்ப்பதில் தோல்வி',
      failedUpdateBarber: 'பார்பரை புதுப்பிப்பதில் தோல்வி',
      failedDeleteBarber: 'பார்பரை நீக்குவதில் தோல்வி',
      deleteBarber: 'பார்பரை நீக்கு',
      confirmDeleteBarber: 'இந்த பார்பரை நீக்க விரும்புகிறீர்களா?',
      deleteService: 'சேவையை நீக்கு',
      confirmDeleteService: 'இந்த சேவையை நீக்க விரும்புகிறீர்களா?',
      deleteSlot: 'நேரத்தை நீக்கு',
      confirmDeleteSlot: 'இந்த நேர இடைவெளியை நீக்க விரும்புகிறீர்களா?',
      cancelReservation: 'முன்பதிவை ரத்து செய்',
      confirmCancelReservation: 'இந்த முன்பதிவை ரத்து செய்ய விரும்புகிறீர்களா?',
      roleUpdated: 'பாத்திரம் {role} ஆக புதுப்பிக்கப்பட்டது',
      reservationStatusUpdated: 'முன்பதிவு நிலை {status} ஆக புதுப்பிக்கப்பட்டது',
      reservationUpdated: 'முன்பதிவு வெற்றிகரமாக புதுப்பிக்கப்பட்டது',
      failedUpdateReservation: 'முன்பதிவை புதுப்பிக்க முடியவில்லை',
      editReservation: 'முன்பதிவைத் திருத்து',
      customerDetails: 'வாடிக்கையாளர் விவரங்கள்',
      newReservation: 'புதிய முன்பதிவு',
      newBookingMade: 'ஒரு புதிய முன்பதிவு செய்யப்பட்டுள்ளது.',
      failedUpdateRole: 'பாத்திரத்தைப் புதுப்பிப்பதில் தோல்வி',
      categories: 'வகைகள்',
      addCategory: 'வகையைச் சேர்க்கவும்',
      categoryExists: 'வகை ஏற்கனவே உள்ளது',
      categoryAdded: 'வகை வெற்றிகரமாகச் சேர்க்கப்பட்டது',
      categoryDeleted: 'வகை வெற்றிகரமாக நீக்கப்பட்டது',
      categoryUpdated: 'வகை வெற்றிகரமாக புதுப்பிக்கப்பட்டது',
      failedUpdateCategory: 'வகையைப் புதுப்பிக்கத் தவறியது',
      categoryInUse: 'பயன்பாட்டில் உள்ள வகையை நீக்க முடியாது',
      addNewCategory: 'புதிய வகையைச் சேர்க்கவும்...',
      selectShopFirst: 'முதலில் ஒரு கடையைத் தேர்ந்தெடுக்கவும்',
      slotExists: 'இந்த நேர இடைவெளி ஏற்கனவே உள்ளது',
      slotAdded: 'நேரம் வெற்றிகரமாக சேர்க்கப்பட்டது',
      failedAddSlot: 'நேரத்தைச் சேர்ப்பதில் தோல்வி',
      slotDeleted: 'நேரம் வெற்றிகரமாக நீக்கப்பட்டது',
      failedDeleteSlot: 'நேரத்தை நீக்குவதில் தோல்வி',
      failedUpdateStatus: 'நிலையைப் புதுப்பிப்பதில் தோல்வி',
      selectBarberFirst: 'தயவுசெய்து முதலில் ஒரு பார்பரைத் தேர்ந்தெடுக்கவும்',
      invalidPriceDuration: 'தவறான விலை அல்லது காலம்',
      serviceAdded: 'சேவை வெற்றிகரமாக சேர்க்கப்பட்டது',
      serviceUpdated: 'சேவை வெற்றிகரமாக புதுப்பிக்கப்பட்டது',
      failedAddService: 'சேவையைச் சேர்ப்பதில் தோல்வி',
      failedUpdateService: 'சேவையைப் புதுப்பிப்பதில் தோல்வி',
      serviceDeleted: 'சேவை வெற்றிகரமாக நீக்கப்பட்டது',
      failedDeleteService: 'சேவையை நீக்குவதில் தோல்வி',
      shopNameRequired: 'கடையின் பெயர் தேவை',
      invalidClosingTime: 'மூடும் நேரம் திறக்கும் நேரத்திற்குப் பிறகு இருக்க வேண்டும்',
      shopAdded: 'கடை வெற்றிகரமாக சேர்க்கப்பட்டது',
      failedAddShop: 'கடையைச் சேர்ப்பதில் தோல்வி',
      confirmDeleteShop: 'இந்த கடையை நீக்க விரும்புகிறீர்களா?',
      shopDeleted: 'கடை வெற்றிகரமாக நீக்கப்பட்டது',
      failedDeleteShop: 'கடையை நீக்குவதில் தோல்வி',
      deleteShop: 'கடையை நீக்கு',
      shopUpdated: 'கடை வெற்றிகரமாக புதுப்பிக்கப்பட்டது',
      failedUpdateShop: 'கடையைப் புதுப்பிப்பதில் தோல்வி',
      remindersNotEnabled: 'இந்த கடைக்கு நினைவூட்டல்கள் செயல்படுத்தப்படவில்லை',
      failedSendReminders: 'நினைவூட்டல்களை அனுப்புவதில் தோல்வி',
      reminderSent: 'நினைவூட்டல் வெற்றிகரமாக அனுப்பப்பட்டது',
      remindersSent: '{count} நினைவூட்டல்கள் வெற்றிகரமாக அனுப்பப்பட்டன',
      reminder: 'நினைவூட்டல்',
      sent: 'அனுப்பப்பட்டது',
      notSent: 'அனுப்பப்படவில்லை',
      sendReminder: 'நினைவூட்டலை அனுப்பு',
      profileNotFoundDesc: 'உங்கள் பயனர் சுயவிவரத்தைக் கண்டறிய முடியவில்லை.',
      accessDeniedDesc: 'இந்த பக்கத்தை அணுக உங்களுக்கு அனுமதி இல்லை.',
      control: 'கட்டுப்பாடு',
      allStatus: 'அனைத்து நிலைகளும்',
      dateTime: 'தேதி மற்றும் நேரம்',
      addSlots: 'நேரங்களைச் சேர்',
      timeSlot: 'நேர இடைவெளி',
      availability: 'கிடைக்கும் தன்மை',
      manageStaffAvailability: 'பணியாளர்கள் மற்றும் கடையின் கிடைக்கும் தன்மையை நிர்வகிக்கவும்',
      slotsForDay: 'தேர்ந்தெடுக்கப்பட்ட நாளுக்கான நேரங்கள்',
      for: 'இதற்காக',
      showingSlotsFor: 'இதற்கான நேரங்கள்:',
      noSlotsAdded: 'இந்த நாளில் நேரங்கள் எதுவும் சேர்க்கப்படவில்லை',
      addFirst: 'நேரங்களைச் சேர்',
      availableSlots: 'கிடைக்கக்கூடிய நேரங்கள்',
      teamManagement: 'குழு மேலாண்மை',
      manageStaffRoles: 'பணியாளர்கள் மற்றும் நிர்வாகப் பாத்திரங்களை நிர்வகிக்கவும்',
      instructions: 'அறிவுறுத்தல்கள்',
      teamInstructions: 'உங்கள் குழு உறுப்பினர்களை நிர்வகிக்கவும் மற்றும் அவர்களை குறிப்பிட்ட கடைகளுக்கு ஒதுக்கவும். நிர்வாகிகள் தங்களுக்கு ஒதுக்கப்பட்ட கடையின் சேவைகள் மற்றும் நேரங்களை நிர்வகிக்கலாம்.',
      menu: 'மெனு',
      items: 'உருப்படிகள்',
      subscription: 'சந்தா',
      currentPlan: 'தற்போதைய திட்டம்',
      renewsOn: 'புதுப்பிக்கப்படும் தேதி',
      manageSubscription: 'சந்தாவை நிர்வகி',
      trialEndsOn: 'சோதனை முடிவடையும் தேதி',
      serviceName: 'சேவையின் பெயர்',
      opening: 'திறப்பு',
      closing: 'மூடல்',
      barber: 'பார்பர்',
      bulkCreateAvailability: 'மொத்தமாக கிடைப்பினை உருவாக்கவும்',
      workingDays: 'வேலை நாட்கள்',
      startDate: 'தொடக்க தேதி',
      endDate: 'முடிவு தேதி',
      quickTips: 'விரைவான குறிப்புகள்',
      tip1: 'குறிப்பிட்ட கிடைப்பினை நிர்வகிக்க முதலில் ஒரு பார்பரைத் தேர்ந்தெடுக்கவும்.',
      tip2: 'முழு மாதத்திற்கான இடங்களை விரைவாக அமைக்க மொத்த ஜெனரேட்டரைப் பயன்படுத்தவும்.',
      tip3: 'சந்திப்பு விவரங்களைக் காண முன்பதிவு செய்யப்பட்ட இடத்தைக் கிளிக் செய்யவும்.',
      noSlotsShort: 'இல்லை',
      addItem: 'பொருளைச் சேர்',
      addShop: 'கடையைச் சேர்',
      automatedCampaigns: 'தானியங்கி பிரச்சாரங்கள்',
      campaignsDesc: 'தானியங்கி சந்தைப்படுத்தல் செய்திகளை அமைக்கவும்',
      promoCodes: 'விளம்பர குறியீடுகள்',
      addPromo: 'விளம்பரத்தைச் சேர்',
      code: 'குறியீடு',
      discount: 'தள்ளுபடி',
      expires: 'காலாவதியாகிறது',
      lowStock: 'குறைந்த இருப்பு',
      inStock: 'இருப்பில் உள்ளது',
      item: 'பொருள்',
      stock: 'இருப்பு',
      minStock: 'குறைந்தபட்ச இருப்பு',
      amount: 'தொகை',
      commissions: 'கமிஷன்கள்',
    },
    home: {
      heroTitle: 'துடிப்பான பார்பர் அனுபவம்',
      heroSubtitle: 'நகரத்தின் சிறந்தவர்களுடன் உங்கள் அடுத்த வெட்டை முன்பதிவு செய்யுங்கள். தொழில்முறை சேவை, நவீன பாணி.',
      bookNow: 'இப்போது முன்பதிவு செய்யுங்கள்',
      ourServices: 'எங்கள் சேவைகள்',
      contactUs: 'எங்களைத் தொடர்பு கொள்ளுங்கள்',
    }
  }
};
